import { useEffect, useMemo, useState, type ComponentProps, type ReactNode } from 'react';
import { AcoesItem, acoesDoMenu, useAcoes, type Pacote } from '../components/Acoes';
import { Arquivados, useSelecao } from '../components/Selecao';
import { useOrganizar } from '../lib/organizar';
import { paraLixeira } from '../lib/lixeira';
import { useBotaoNovo } from '../lib/botaoNovo';
import { marcarPerguntado, temPartes, usePartes } from '../lib/partes';
import { PartesDaAba } from '../components/PartesDaAba';
import { ReceivedBoxes } from '../components/BoxCard';
import { ValorPago } from '../components/Caixinhas';
import { setAberto, useRecolherTudo } from '../lib/recolher';
import { useSimulacao } from '../lib/compartilhar';
import { EnviarAoPlanner } from '../components/EnviarAoPlanner';
import { listaComData } from '../lib/enviarPlanner';
import { listaInteiraPermitida } from '../lib/migrarListas';
import { enviarVariosAoPlanner, perguntarEnvio, profDe } from '../lib/envioPlanner';   // texto 10 (E1)
import { paraPlanner } from '../lib/envioEstado';                 // texto 10 (E3/E4)
import { EscolherMoeda } from '../components/EscolherMoeda';
import { RelatoriosGerados } from '../components/RelatoriosGerados';
import { Recolhivel } from '../components/Recolhivel';
import { useIsFocused } from 'expo-router';
import { ActivityIndicator, Alert, BackHandler, Image, Modal, Pressable, ScrollView, Switch, Text, TextInput, View } from 'react-native';
import type { ModId, Reg } from '../store/types';
import { getDoc, useDoc } from '../store/store';
import { descendentes, excluirReg, mudarCampos, mudarReg, novoReg, ordenarRegs, regPorId, salvarReg, salvarVarios } from '../store/modulos';
import { Ordenavel, ScrollArrastavel, semArrastar } from '../components/Arrastar';
import { ItemDeslizar, excluirPequeno, inserirEm } from '../components/ItemDeslizar';
import { DEFS } from '../modulos/defs';
import type { Campo, Colecao, Ctx } from '../modulos/tipos';
import { contagem, dataBr, din, junta, moedaDo, num, op, simboloMoeda } from '../modulos/util';
import { AnexosEditor, fonteImagem, iconeAnexo } from '../components/Anexos';
import { useCofreSheet } from '../components/CofreSheet';
import { useFonteIA } from '../components/FonteIA';
import { cifrado, cifrar, decifrar, useCofre } from '../lib/cofre';
import { useTaskSheet } from '../components/TaskSheet';
import { PickerModal } from '../components/Pickers';
import { FusoSheet } from '../components/FusoSheet';
import { ActionSheet, Btn, Chip, Field, Screen, Sheet, TextoMais, type Action } from '../components/ui';
import { CampoRico, chaveRica } from '../components/CampoRico';
import { PaginaRolavel } from '../components/PaginaEditor';
import { C, PALETA, aba, alpha, useCores } from '../components/theme';
import { CampoIcone } from '../components/EscolherIcone';
import { brYmd, hm, horaTela, norm, parseYmd, today, uid, ymd } from '../lib/date';
import { dinheiro, lerValor, moedaPadrao } from '../lib/moeda';
import { apagarAnexo, abreAqui, lerBase64, protegerAnexo, temLocal } from '../lib/anexos';
import { escolherEGuardar } from '../lib/nuvem';
import { iaPronta } from '../lib/ia';
import { lerFicha } from '../lib/iaUsos';
import { nomeFuso } from '../lib/fuso';
import { N_, t } from '../lib/i18n';
import { durLabel } from '../lib/time';
import { abrirRota, abrirSite, email, juntarLocal, ligar, separarLocal, whatsapp } from '../lib/mapa';
import { abrirGrupos, confirmarCompartilhar, convidarPara, criarGrupoPara } from '../components/Grupos';
import { membrosDe, meuId, useMeusGrupos, type Membro } from '../lib/grupos';
import { acessoDaFicha, marcarSoVerNaAba, grupoDaAba, grupoPorId, ligarCompartilhamento, ocultarDe, papeisDoGrupo, podeCriar } from '../lib/compartilhar';
import { ehColaborativa, papeisDaAba, segDoCampo } from '../modulos/papeis';
import { useCaixas } from '../lib/caixas';
import { larguraColuna, useColunas } from '../lib/tamanho';
import { AlertasDaFicha } from '../components/AlertasDaFicha';
import { campoComAlerta, horaDoCampo } from '../modulos/alertas';
import { emailOk, formatarDoc, mascararDoc } from '../modulos/clientes';
import { migrarClientes } from '../lib/clientes';
import { useConfirmarIdentidade } from '../components/ConfirmarIdentidade';

/** Endereço do registro (ou do registro dono: a escola da criança, o endereço da obra...). */
function enderecoDe(r: Reg | null, ctx: Ctx, limite = 3): string {
  if (!r || !limite) return '';
  const col = DEFS[ctx.mod].colecoes.find(c => c.id === r.col);
  const f = col?.campos.find(x => x.t === 'endereco' && String(r.c[x.k] ?? '').trim());
  return f ? String(r.c[f.k]).trim() : enderecoDe(ctx.porId(r.pai), ctx, limite - 1);
}

/** Botões de contato: rota até o endereço, ligar, WhatsApp, e-mail e site. */
function Contatos({ r, c, cor, ctx }: { r: Reg; c: Colecao; cor: string; ctx: Ctx }) {
  // na viagem, o endereço do roteiro ganha a cidade do destino ("Belém" vira "Belém, Lisboa, Portugal")
  const destino = ctx.mod === 'viagens' ? String(ctx.porId(r.pai)?.c.destino ?? '') : '';
  const comCidade = (v: string) => (destino && !v.toLowerCase().includes(destino.split(',')[0].toLowerCase()) ? `${v}, ${destino}` : v);
  const bts: { k: string; l: string; f: () => void }[] = [];
  const varios = (tipo: string) => c.campos.filter(x => x.t === tipo && String(r.c[x.k] ?? '').trim()).length > 1;
  for (const f of c.campos) {
    const v = String(r.c[f.k] ?? '').trim();
    if (!v) continue;
    const rot = (padrao: string) => (varios(f.t) ? t(f.r) : padrao);
    if (f.t === 'endereco') bts.push({ k: f.k, l: '🗺️ ' + rot(t('Rota')), f: () => abrirRota(comCidade(v)) });
    if (f.t === 'telefone') { bts.push({ k: f.k, l: '📞 ' + rot(t('Ligar')), f: () => ligar(v) }); bts.push({ k: f.k + 'w', l: '💬 WhatsApp', f: () => whatsapp(v) }); }
    if (f.t === 'email') bts.push({ k: f.k, l: '✉️ ' + t('E-mail'), f: () => email(v) });
    if (f.t === 'link') bts.push({ k: f.k, l: '🌐 ' + rot(t('Abrir site')), f: () => abrirSite(v) });
  }
  if (!bts.length) return null;
  return (
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
      {bts.map(b => (
        <Pressable key={b.k} onPress={b.f} hitSlop={4} style={{ paddingHorizontal: 10, paddingVertical: 6, borderRadius: 12, backgroundColor: alpha(cor, 0.15) }}>
          <Text style={{ color: C.text, fontSize: 12, fontWeight: '600' }}>{b.l}</Text>
        </Pressable>
      ))}
    </View>
  );
}

const erro = (e: any) => Alert.alert(t('Não foi possível'), String(e?.message ?? e));

/** Monta o contexto usado pelas definições (títulos, resumos, painéis). */
export function useCtx(mod: ModId): Ctx {
  const doc = useDoc(mod);
  const { corDe } = useCores();
  const fin = useDoc('financas');
  return useMemo(() => {
    const regs = doc?.regs ?? [];
    const mapa = new Map(regs.map(r => [r.id, r]));
    return {
      mod, regs, cor: corDe(mod), moeda: fin.moeda || moedaPadrao(),
      filhos: (pai: string, col?: string) => regs.filter(r => r.pai === pai && (!col || r.col === col)),
      porId: (id: string | null | undefined) => (id ? mapa.get(id) ?? null : null),
    };
  }, [doc, mod, corDe, fin.moeda]);
}

// nome do grupo de cada aba no menu da ficha
/** Listas que não têm grupo com convite (0.68: "Onde trabalho" é só seu). */
const SEM_GRUPO = ['instituicao', 'pasta'];   // texto 14 K: a pasta é só sua (atalhos)
const ROTULO_GRUPO: Partial<Record<ModId, string>> = {
  professor: N_('Grupo da turma (convidar alunos)'), leitura: N_('Grupo de leitura (convidar)'), viagens: N_('Grupo da viagem (convidar quem vai)'),
  eventos: N_('Grupo do evento (convidar)'), obras: N_('Grupo da obra (equipe)'), mudanca: N_('Grupo da mudança (família e ajudantes)'),
  hospedagem: N_('Equipe do imóvel (limpeza e co-anfitrião)'), consultorio: N_('Equipe do consultório'),
  portal: N_('Portal do cliente (convidar o cliente)'), diario: N_('Compartilhar este projeto (convidar)'),
  freelancer: N_('Grupo do cliente'), familia: N_('Grupo da família'), saude: N_('Grupo de cuidado (família ou profissional)'),
};
/** Itens grandes (B1, 0.65): pedem confirmação E a senha (ou biometria) para excluir, e não se excluem em grupo. */
const GRANDES: Partial<Record<ModId, string[]>> = {
  consultorio: ['paciente', 'instituicao'], viagens: ['viagem'], obras: ['obra'], professor: ['turma', 'instituicao'], hospedagem: ['espaco', 'imovel'], veiculos: ['veiculo'],
  eventos: ['evento'], mudanca: ['mudanca'], familia: ['pet', 'pessoa', 'membro'], freelancer: ['cliente'],
  // texto 26 (B6, REGRAS 1.7): também cliente (Meus clientes e Portal) e pasta da aba Leitura (excluir a pasta não apaga os itens)
  clientes: ['cliente'], portal: ['cliente'], leitura: ['pasta'],
};
/** Texto 26 (B7, REGRAS 1.8): sem "Duplicar" (só Arquivar): pagamento já feito (o ☑ pago marcado) e o que a aba disser. */
const semDuplicarDe = (r: Reg, c: Colecao, ctx: Ctx) => (!!c.check && !!c.pagamento && !!r.c[c.check]) || !!c.semDuplicar?.(r, ctx);
const ehGrande = (mod: ModId, r: Reg) => (GRANDES[mod] ?? []).includes(r.col) && !r.pai;
/** Abas com dados de paciente e prontuário: as mesmas regras de privacidade valem na Lixeira (texto 05 C3). */
const SIGILO: ModId[] = ['consultorio', 'saude'];
// abas em que o grupo é da aba inteira (a equipe), não de cada ficha: no consultório, cada ficha é um paciente
const GRUPO_SO_DA_ABA: ModId[] = ['consultorio'];
const VAZIA: Colecao = { id: '_', nome: '', um: '', icone: '', campos: [], titulo: () => '', semNovo: true };

// ─── Texto 23 (H): Nota rápida em todas as listas ───────────────────────────────────────────────
// O + oferece "Item completo" (o formulário de sempre) e "📝 Nota rápida": só um título opcional e a caixa igual à da
// aba Anotações. A nota fica na mesma lista (mesmo pai), numa coleção irmã "<lista>__nota", para não entrar nas contas,
// nos resumos nem nos filtros da lista (ex.: não vira convidado nem gasto). Nada vai sozinho para o My Planner.
const SUF_NOTA = '__nota';
export const ehNota = (r: Reg) => r.col.endsWith(SUF_NOTA);
const primeiraLinhaNota = (r: Reg) => String(r.c.notas ?? '').split('\n').map(x => x.replace(/^[\s•☐☑✓-]+/, '').trim()).find(Boolean) ?? '';
const colsNota = new Map<string, Colecao>();
function colNota(base: Colecao): Colecao {
  let c = colsNota.get(base.id);
  if (!c) {
    c = { id: base.id + SUF_NOTA, pai: base.pai, nome: N_('Nota rápida'), um: N_('Nota rápida'), icone: '📝',
      campos: [{ k: 'titulo', r: N_('Título (opcional)'), t: 'texto' }, { k: 'notas', r: N_('Conteúdo da nota'), t: 'longo' }],
      titulo: r => String(r.c.titulo ?? '').trim() || primeiraLinhaNota(r) || t('Nota rápida'),
      tituloNovo: N_('📝 Nota rápida'), tituloEditar: N_('📝 Nota rápida'), semEnviarLista: true };
    colsNota.set(base.id, c);
  }
  return c;
}
/** A coleção de um registro (inclusive as notas rápidas de uma lista). */
function colecaoDe(mod: ModId, col: string): Colecao | null {
  const d = DEFS[mod].colecoes;
  if (col.endsWith(SUF_NOTA)) { const b = d.find(c => c.id === col.slice(0, -SUF_NOTA.length)); return b ? colNota(b) : null; }
  return d.find(c => c.id === col) ?? null;
}
/** Data daqui a n meses (atalhos "Em 1 mês", "Em 3 meses"). */
function emMeses(n: number) { const d = new Date(); d.setMonth(d.getMonth() + n); return ymd(d); }
const iconeDe = (col: Colecao, r: Reg) => (typeof col.icone === 'function' ? col.icone(r) : col.icone);

const ordemPorData = (col: Colecao) => { const o = col.ordem ?? (col.data ? 'data' : 'criado'); return o === 'data' || o === 'data-desc' || typeof o === 'function'; };
function ordenar(col: Colecao, l: Reg[], ctx: Ctx) {
  const o = col.ordem ?? (col.data ? 'data' : 'criado');
  if (typeof o === 'function') return [...l].sort(o);
  const ch = (r: Reg) => (col.data ? String(r.c[col.data] ?? '9999') : r.criado);
  const base = [...l].sort((a, b) => o === 'data' ? ch(a).localeCompare(ch(b)) : o === 'data-desc' ? ch(b).localeCompare(ch(a))
    : o === 'titulo' ? col.titulo(a, ctx).localeCompare(col.titulo(b, ctx)) : a.criado.localeCompare(b.criado));
  // ordem escolhida arrastando vale nas listas que não são por data (0.34)
  if (!ordemPorData(col)) base.sort((a, b) => (a.ordem ?? 1e9) - (b.ordem ?? 1e9));
  return col.feito ? [...base.filter(r => !col.feito!(r)), ...base.filter(r => col.feito!(r))] : base;
}

/** Texto curto do valor de um campo (para o resumo da ficha). */
function valorTxt(f: Campo, v: any, ctx: Ctx, c?: Record<string, any>) {
  if (v === '' || v == null || (Array.isArray(v) && !v.length)) return '';
  switch (f.t) {
    case 'data': return dataBr(v);
    case 'hora': return horaTela(v);
    case 'dinheiro': return din(v, moedaDo(c, f.k, ctx.moeda));
    case 'numero': return `${v}${f.un ? ' ' + t(f.un) : ''}`;
    case 'opcao': return op(v);
    case 'multi': return (v as string[]).map(op).join(', ');
    case 'sim': return v ? '✓' : '';
    case 'doc': return mascararDoc(v);   // texto 25 (C4): CPF/CNPJ nunca aparecem completos fora do 👁️
    case 'fuso': return nomeFuso(v);
    case 'ref': { const r = ctx.porId(v); if (!r) return ''; const c = DEFS[ctx.mod].colecoes.find(x => x.id === r.col); return c ? c.titulo(r, ctx) : ''; }
    case 'cofre': return '🔒';
    case 'cor': return '';   // a cor aparece no próprio cartão
    case 'icone': return '';
    case 'estrelas': return '★'.repeat(num(v));
    default: return String(v);
  }
}

/** convidado (0.65, B11): a aba não está no plano, mas a pessoa participa de um grupo dela. Vê e usa, de graça, só o que
 *  foi compartilhado com ela (fichas do grupo); não cria fichas próprias. */
/** Cópia dos campos para Duplicar: sem os alarmes ligados à ficha original (senão a cópia tomava os alarmes dela, 0.77). */
/** Texto 21 (A1) e 23 (C5): abas em que quem não é pagante (convidado) só visualiza, na tela e no servidor. */
const SO_VER_CONVIDADO: ModId[] = ['leitura', 'eventos'];
const semTarefas = (c: Record<string, any>) => { const { _tarefas, ...resto } = c; return resto; };

export default function Modulo({ id, item, onBack, convidado }: { id: ModId; item?: string; onBack: () => void; convidado?: boolean }) {
  const caixas = useDoc('anotacoes').boxes;
  marcarSoVerNaAba(id, !!convidado && SO_VER_CONVIDADO.includes(id));   // texto 21 (A1) e 23 (C5): convidado sem a aba Leitura/Eventos só visualiza
  const def = DEFS[id];
  const ctx = useCtx(id);
  const m = useCaixas();
  const cor = ctx.cor;
  const sheet = useTaskSheet();
  const [pilha, setPilha] = useState<string[]>([]);       // fichas abertas (ex.: a viagem e, dentro dela, o roteiro)
  const [abaSel, setAbaSel] = useState<Record<string, string>>({});
  const [busca, setBusca] = useState('');
  const [filtro, setFiltro] = useState<number>(-1);
  const [ficha, setFicha] = useState<Reg | null>(null);
  const [menu, setMenu] = useState<{ t: string; a: Action[] } | null>(null);
  const [relatorio, setRelatorio] = useState(false);   // texto 16: relatório da aba
  const { enviar: enviarPacote, confirmar } = useAcoes();
  const org = useOrganizar(id);
  const colunas = useColunas(340);   // texto 12 B: no navegador largo, os cartões em 2 ou mais colunas

  const pai = ctx.porId(pilha[pilha.length - 1]);
  const partes = usePartes(id);
  // 0.65 (Texto 3 D): só as partes que a pessoa usa (se esconder todas de uma tela, mostra todas)
  const colsTodas = def.colecoes.filter(c => (pai ? c.pai === pai.col : !c.pai) && (!c.mostrarSe || c.mostrarSe(ctx, pai)));
  const colsAqui = colsTodas.some(c => partes.visivel(c.id)) ? colsTodas.filter(c => partes.visivel(c.id)) : colsTodas;
  const [perguntar, setPerguntar] = useState(() => !partes.perguntado && !convidado && temPartes(id) && !getDoc(id).regs?.length);
  const chave = pai?.id ?? 'raiz';
  // ficha aberta como página sem lista dentro (matéria do curso, 0.56): fica uma coleção vazia só para não quebrar a tela
  const semCol = !colsAqui.length;
  const col = colsAqui.find(c => c.id === abaSel[chave]) ?? colsAqui[0] ?? VAZIA;
  const temFilhos = (c: string) => def.colecoes.some(x => x.pai === c || (x.id === c && x.pagina));

  // texto 25: Meus clientes, Portal e Freelancer usam a mesma lista (liga e cria o que faltar ao abrir)
  useEffect(() => { if (id === 'clientes' || id === 'portal' || id === 'freelancer') { try { migrarClientes(); } catch { /* segue */ } } }, [id]);
  // valores antigos (ex.: "Quero ver" virou "Quero assistir")
  useEffect(() => { def.migrar?.(ctx.regs).forEach(x => mudarCampos(id, x.id, x.c)); }, [ctx.regs]); // eslint-disable-line react-hooks/exhaustive-deps

  // abrir pelo vínculo de uma tarefa (?item=)
  useEffect(() => {
    if (!item) return;
    const r = ctx.porId(item);
    if (!r) return;
    const caminho: string[] = [];
    let p = ctx.porId(r.pai);
    while (p) { caminho.unshift(p.id); p = ctx.porId(p.pai); }
    if (temFilhos(r.col)) caminho.push(r.id);
    setPilha(caminho);
    if (!temFilhos(r.col)) { setAbaSel(s => ({ ...s, [r.pai ?? 'raiz']: r.col })); setFicha(r); }
  }, [item]); // eslint-disable-line react-hooks/exhaustive-deps

  // a ficha aberta foi apagada (por outro aparelho, por exemplo): volta
  useEffect(() => { if (pilha.length && !pai) setPilha(p => p.filter(x => ctx.porId(x))); }, [pai, pilha, ctx]);

  const voltar = () => { if (pilha.length) { setPilha(p => p.slice(0, -1)); setBusca(''); setFiltro(-1); } else onBack(); };
  const focoModulo = useIsFocused();   // 0.71: o voltar do Android só age na aba que está na tela
  useEffect(() => {
    if (!pilha.length || !focoModulo) return;
    const s = BackHandler.addEventListener('hardwareBackPress', () => { voltar(); return true; });
    return () => s.remove();
  }); // eslint-disable-line react-hooks/exhaustive-deps

  const doColTudo = ctx.regs.filter(r => (r.col === col.id || r.col === col.id + SUF_NOTA) && (col.pai ? r.pai === pai?.id : !r.pai) && (!convidado || !!r.g || !!pai?.g));
  const doCol = doColTudo.filter(r => !ehNota(r));   // os dados da lista (contas, resumos, filtros): sem as notas rápidas
  // fichas de outra coleção mostradas nesta lista (0.59: lugares com dia aparecem no roteiro)
  const extras = col.incluir?.(ctx, pai) ?? [];
  const cabecalhos = new Map<string, string>();   // 0.60: título do bloco antes do primeiro item de cada grupo (ex.: dia do roteiro)
  useSimulacao();   // texto 27 (B4.6): "Ver como o cliente" redesenha a tela com o papel do convidado
  const gruposAbertos = useRecolherTudo();         // texto 27 (sobras): grupos recolhidos (ex.: meses antigos dos documentos)
  const chaveGrupo = (g: string) => `grp:${id}:${col.id}:${pai?.id ?? ''}:${g}`;
  const colDe = (r: Reg) => (r.col === col.id ? col : colecaoDe(id, r.col) ?? col);
  const q = norm(busca.trim());
  const filtros = [...(col.filtros ?? []), ...(col.filtrosDe?.(doCol, ctx) ?? [])];
  // texto 19 (A1/A2): filtro "Arquivados" junto dos outros (como em Anotações); a linha recolhida no fim continua
  const verArq = !!col.filtroArquivados && filtro === -2;
  const filtrada = (col.reordenarIncluidos ? [...ordenar(col, doColTudo, ctx), ...extras] : ordenar(col, [...doColTudo, ...extras], ctx))
    .filter(r => { const cr = colDe(r); return !q || norm(junta(cr.titulo(r, ctx), cr.sub?.(r, ctx), ...cr.campos.filter(f => f.t !== 'cofre').map(f => valorTxt(f, r.c[f.k], ctx, r.c)), cr.buscaExtra?.(r))).includes(q)
      || (!!cr.buscaExtra && /\d{3}/.test(q) && cr.buscaExtra(r).includes(q.replace(/\D/g, ''))); })
    .filter(r => filtro < 0 || !filtros[filtro] || (!ehNota(r) && filtros[filtro][1](r, ctx)));
  // arquivados saem da lista principal; fixados aparecem primeiro (0.65)
  const sep = org.separar(filtrada, r => r.id);
  const listaToda = verArq ? sep.arquivados : sep.ativos, arquivados = verArq ? [] : sep.arquivados;
  const naoArq = col.filtroArquivados ? doColTudo.filter(r => !org.arquivado(r.id)) : doColTudo;   // contagens dos filtros sem os arquivados
  // texto 13 B: fichas que vão para a linha recolhida no fim (ex.: livros abandonados)
  const lista = col.separado ? listaToda.filter(r => !col.separado!.f(r)) : listaToda;
  // texto 27 (sobras): o 1º grupo (ex.: o mês mais recente) começa aberto; os outros recolhidos
  const grupo1 = col.grupoDe && lista.length ? col.grupoDe(lista[0]) : '';
  const grupoAberto = (g: string) => gruposAbertos[chaveGrupo(g)] ?? g === grupo1;
  const separados = col.separado ? listaToda.filter(r => col.separado!.f(r)) : [];

  // auditoria 0.77: a regra de senha da lista (col.grande) vale também na seleção; item só mostrado aqui (pasta, roteiro) sai só daqui
  const sel = useSelecao({ itens: lista, chave: r => r.id, cor, grande: r => ehGrande(id, r) || !!colDe(r).grande?.(r, ctx) || !acessoDaFicha(r).editar(colDe(r).id),
    // texto 19 (F3) e 21 (B3): em todas as abas, "Selecionar todos" e "Enviar" os selecionados para o My Planner de uma vez
    genero: 'm', extras: [...(col.acoesSelecao?.(ctx, pai) ?? []), { label: '📅 ' + t('Enviar para o My Planner'), onPress: (ids: string[]) => enviarVariosAoPlanner(ids.map(x => {
      const r = ctx.porId(x); return { aba: id, id: x, titulo: r ? colDe(r).titulo(r, ctx) || t(colDe(r).um) : '' }; })) }],
    onArquivar: ids => org.arquivar(ids),
    onExcluir: ids => ids.forEach(x => {
      const r = ctx.porId(x); if (!r || !acessoDaFicha(r).editar(colDe(r).id)) return;
      if (colDe(r) !== col && col.tirarIncluido) col.tirarIncluido(r, ctx, pai); else excluirJa(r, colDe(r));
    }) });
  // o + flutuante cria um item desta lista (0.65)
  // texto 23 (H1): o + oferece "Item completo" e "📝 Nota rápida" (nas listas sem formulário próprio, como a chamada, só o item)
  const comNota = !col.semNovo && !col.lista && col.id !== 'chamada';
  useBotaoNovo(!col.semNovo && (!convidado || !!pai?.g) && podeCriar(id, col.id, col.pai ? pai : null) ? { rotulo: t('Adicionar {nome}', { nome: t(col.um).toLowerCase() }),
    fn: () => (comNota ? setMenu({ t: t('Adicionar {nome}', { nome: t(col.um).toLowerCase() }), a: [
      { label: '📋 ' + t('Item completo'), onPress: () => novo() },
      { label: '📝 ' + t('Nota rápida'), onPress: () => novaNota() }] }) : novo()) } : null);
  // auditoria 0.77: a tela vazia vem depois dos hooks (antes, os hooks abaixo podiam mudar de quantidade e derrubar a tela)
  if (semCol && !pai) return <Screen title={aba(id).nome} onBack={onBack}><Text style={{ color: C.sub }}>…</Text></Screen>;
  function novo(c: Record<string, any> = {}, colecao: Colecao = col) {
    if (!podeCriar(id, colecao.id, colecao.pai ? pai : null)) return Alert.alert(t('Só leitura'), t('O seu papel no grupo não permite incluir aqui.'));
    const base = colecao.novo?.(ctx, pai) ?? {};
    setFicha(novoReg(colecao.id, colecao.pai ? pai?.id ?? null : null, { ...base, ...c }));
  }
  function novaNota() {
    if (!podeCriar(id, col.id, col.pai ? pai : null)) return Alert.alert(t('Só leitura'), t('O seu papel no grupo não permite incluir aqui.'));
    setFicha(novoReg(col.id + SUF_NOTA, col.pai ? pai?.id ?? null : null, { titulo: '', notas: '' }));
  }
  function abrir(r: Reg) {
    if (ehNota(r)) { setFicha(r); return; }
    // texto 28 (C4): documento sensível: pede a senha do app (ou biometria) antes de abrir
    if (colDe(r).pedirSenha?.(r)) { confirmar(t('Confirme para abrir este documento')).then(ok => { if (ok) abrirJa(r); }, () => {}); return; }
    abrirJa(r);
  }
  function abrirJa(r: Reg) {
    try { colDe(r).aoAbrir?.(r, ctx); } catch { /* segue */ }   // texto 27 (B4): ex.: "Visto pelo cliente" ao abrir o documento
    if (temFilhos(r.col)) { setPilha(p => [...p, r.id]); setBusca(''); setFiltro(-1); }
    else setFicha(r);
  }
  function colocarNaAgenda(r0: Reg, c: Colecao) {
    const r = regPorId(id, r0.id) ?? r0;   // 0.77: o "ao salvar" pode já ter criado a tarefa (consulta): usa a ficha atualizada
    const existe = r.tarefa ? getDoc('planner').tasks.find(x => x.id === r.tarefa) : null;
    if (existe) return sheet.open({ task: existe });
    // texto 21 (B2): listas com data e aviso (que antes só iam inteiras) também vão item por item, pela data com aviso
    const bruto = c.agenda?.(r, ctx) ?? (listaComData(c) ? {} : null);
    if (!bruto) return;
    const p = Object.fromEntries(Object.entries(bruto).filter(([, v]) => v !== undefined)) as typeof bruto;
    const data = (c.data ? r.c[c.data] : null) || dataComAviso(c, r) || null;
    const end = enderecoDe(r, ctx);
    sheet.open({
      date: p.start ?? data ?? today(),
      // texto 10 (E4): das abas de saúde vai só título, horário e profissional; (E3) aguardando confirmação = provisório
      prefill: paraPlanner(id, r.id, { area: id, emojiManual: true, emoji: iconeDe(c, r), title: c.titulo(r, ctx), start: data || today(),
        link: { aba: id, id: r.id, label: c.titulo(r, ctx) }, ...p, notes: end && !String(p.notes ?? '').startsWith('📍') ? juntarLocal(end, String(p.notes ?? '')) : p.notes ?? '' }, profDe(id, r.c)),
      onSaved: tk => mudarReg(id, r.id, x => ({ ...x, tarefa: tk.id })),
    });
  }
  function enviarParaGrupo(r: Reg, c: Colecao) {
    let topo = r; for (let p = ctx.porId(r.pai); p; p = ctx.porId(p.pai)) topo = p;
    const bruto = c.agenda?.(r, ctx) ?? {};
    const p = Object.fromEntries(Object.entries(bruto).filter(([, v]) => v !== undefined)) as typeof bruto;
    const data = (p.start ?? (c.data ? r.c[c.data] : '')) || undefined;
    const link = c.campos.filter(f => f.t === 'link').map(f => String(r.c[f.k] ?? '').trim()).find(Boolean);
    // sem data (livro, receita, lugar...): vai como material, com o resumo da ficha
    const resumo = c.campos.filter(f => !['cofre', 'link', 'endereco'].includes(f.t)).map(f => [f, valorTxt(f, r.c[f.k], ctx, r.c)] as const)
      .filter(([, v]) => v).map(([f, v]) => `${t(f.r)}: ${v}`).join('\n');
    const local = enderecoDe(r, ctx);
    let notas = separarLocal(String(p.notes ?? '')).resto.trim();
    if (local && notas === local.trim()) notas = '';   // o endereço já vai no campo do local

    abrirGrupos({ aba: id, ref: topo.id, envio: { titulo: p.title ?? (c.titulo(r, ctx) || t(c.um)), tipo: data && c.agenda ? 'compromisso' : 'material', dados: {
      data: c.agenda ? data : undefined, hora: c.agenda ? p.time ?? undefined : undefined, fuso: p.tz ?? undefined, local: local || undefined, link, emoji: iconeDe(c, r),
      texto: (c.agenda ? notas : [resumo, notas].filter(Boolean).join('\n')) || undefined } } });
  }
  function pacoteDe(r: Reg, cr: Colecao): Pacote {
    const pode = acessoDaFicha(r).editar(cr.id);
    const ag = cr.agenda?.(r, ctx);
    const resumo = cr.campos.filter(f => !['cofre', 'link', 'endereco', 'data'].includes(f.t)).map(f => [f, valorTxt(f, r.c[f.k], ctx, r.c)] as const)
      .filter(([, v]) => v).map(([f, v]) => `${t(f.r)}: ${v}`).join('\n');
    return { titulo: cr.titulo(r, ctx) || t(cr.um), emoji: iconeDe(cr, r), origem: id, data: ag?.start ?? (cr.data ? r.c[cr.data] || null : null),
      hora: ag?.time ?? null, itens: r.itens?.map(i => i.text), texto: ehNota(r) ? String(r.c.notas ?? '') : resumo, extras: pode ? cr.enviarPara?.(r, ctx) : undefined };
  }
  function opcoes(r: Reg, c: Colecao) {
    const ac = acessoDaFicha(r), pode = ac.editar(c.id);
    const a: Action[] = [{ label: pode ? '✏️ ' + t('Editar') : '👁️ ' + t('Ver'), onPress: () => setFicha(r) }];
    if (c.agenda || listaComData(c)) a.push({ label: '📅 ' + (r.tarefa && getDoc('planner').tasks.some(x => x.id === r.tarefa) ? t('Ver no My Planner') : t('Colocar no My Planner')), onPress: () => colocarNaAgenda(r, c) });
    if (pode) a.push(...(c.acoes?.(r, ctx, (tt, aa) => setTimeout(() => setMenu({ t: tt, a: aa }), 350), confirmar) ?? []));
    // grupos: mandar este compromisso para a turma, os pacientes, a equipe...
    // 0.65: Enviar (para outra aba) e Fixar no topo ficam no menu ⋮
    if (c.id !== 'chamada') a.push(...acoesDoMenu(enviarPacote, { pacote: () => pacoteDe(r, c), fixar: r.pai === (pai?.id ?? undefined) || (!r.pai && !pai) ? { on: org.fixado(r.id), alternar: () => org.alternarFixo(r.id) } : undefined, lembrar: { aba: id, id: r.id, titulo: c.titulo(r, ctx) || t(c.um) } }));
    if (c.id !== 'chamada') a.push({ label: '👥 ' + t('Enviar para um grupo'), onPress: () => enviarParaGrupo(r, c) });
    if (pode) a.push({ label: '📦 ' + (org.arquivado(r.id) ? t('Tirar do arquivo') : t('Arquivar')), onPress: () => (org.arquivado(r.id) ? org.restaurar(r.id) : org.arquivar(r.id)) });
    if (!c.pai && (temFilhos(c.id) || c.id === 'cliente') && !SEM_GRUPO.includes(c.id) && !GRUPO_SO_DA_ABA.includes(id) && (!r.g || ac.admin)) a.push({ label: '👥 ' + t(c.nomeGrupo ?? ROTULO_GRUPO[id] ?? N_('Grupo com convite (turma, equipe, convidados)')), onPress: () => abrirGrupos({ aba: id, novo: { nome: c.titulo(r, ctx), ref: r.id } }) });
    if (pode && !semDuplicarDe(r, c, ctx)) a.push({ label: '⧉ ' + t('Duplicar'), onPress: () => salvarReg(id, { ...novoReg(c.id, r.pai, semTarefas(r.c)), itens: r.itens?.map(i => ({ ...i, id: uid(), done: false })) }) });
    if (pode) a.push({ label: t('Excluir'), destructive: true, onPress: () => excluir(r, c) });
    setMenu({ t: c.titulo(r, ctx) || t(c.um), a });
  }
  /** Item de outra coleção mostrado aqui (ex.: lugar no roteiro, 0.60): tirar só daqui ou excluir de vez. */
  function tirarDaqui(r: Reg, c: Colecao) {
    Alert.alert(c.titulo(r, ctx) || t(c.um), t('Tirar só desta lista ou excluir de vez?'), [
      { text: t('Cancelar'), style: 'cancel' },
      { text: t('Tirar daqui'), onPress: () => col.tirarIncluido?.(r, ctx, pai) },
      { text: t('Excluir de vez'), style: 'destructive', onPress: () => excluir(r, c) }]);
  }
  /** Exclui de vez (vai para a lixeira de 30 dias, com tudo o que está dentro). */
  function excluirJa(r: Reg, c: Colecao) {
    const tudo = [r, ...descendentes(id, r.id)];
    // grande: na Lixeira, "Excluir de vez" e "Esvaziar a lixeira" pedem a senha (texto 05 C3).
    // sigilo (paciente e prontuário): na Lixeira aparece só o tipo (com o nome na ficha de cima, como nas listas),
    // e Restaurar/Excluir de vez pedem a confirmação de identidade; os campos do cofre continuam criptografados.
    const sigilo = SIGILO.includes(id);
    const nome = c.titulo(r, ctx) || t(c.um);
    const titulo = !sigilo ? nome : c.pai ? t(c.um) : `${t(c.um)} · ${nome}`;
    paraLixeira(id, 'reg', titulo, tudo, { mod: id, ...(ehGrande(id, r) ? { grande: true } : {}), ...(sigilo ? { sigilo: true } : {}) });
    c.aoExcluir?.(r, ctx);
    excluirReg(id, r.id); setFicha(null);
    if (pilha.includes(r.id)) setPilha(p => p.slice(0, p.indexOf(r.id)));
  }
  function excluir(r: Reg, c: Colecao) {
    const n = descendentes(id, r.id).length;
    const grande = ehGrande(id, r) || !!c.grande?.(r, ctx);
    Alert.alert(t('Excluir "{nome}"?', { nome: c.titulo(r, ctx) || t(c.um) }), [c.avisoExcluir?.(r, ctx) ?? '', n ? t('Também serão excluídos {n} item(ns) dentro dele.', { n }) : '', t('Fica 30 dias na lixeira.')].filter(Boolean).join('\n\n'), [
      { text: t('Cancelar'), style: 'cancel' },
      { text: t('Excluir'), style: 'destructive', onPress: async () => {
        // itens grandes (turma, paciente, viagem inteira...) pedem também a senha ou a biometria
        if (grande && !(await confirmar(t('Confirme para excluir')))) return;
        excluirJa(r, c);
      } }]);
  }

  const colPai = pai ? def.colecoes.find(c => c.id === pai.col)! : null;
  const resumo = col.resumo?.(doCol, ctx, pai);
  const Painel = col.painel, Lista = col.lista;
  const Topo = colPai?.painelTopo;
  const titulo = pai && colPai ? colPai.titulo(pai, ctx) || t(colPai.um) : aba(id).nome;

  return (
    <Screen title={titulo} onBack={voltar} right={
      <View style={{ flexDirection: 'row', gap: 8 }}>
        {!pai && <Pressable onPress={() => abrirGrupos({ aba: id })} accessibilityLabel={t('Grupos e convites')} style={{ width: 38, height: 38, borderRadius: 19, backgroundColor: C.card, alignItems: 'center', justifyContent: 'center' }}>
          <Text style={{ fontSize: 18 }}>👥</Text></Pressable>}

      </View>}>
      {!pai && convidado && (
        <View style={{ backgroundColor: alpha(cor, 0.12), borderRadius: 14, padding: 12, gap: 4 }}>
          <Text style={{ color: C.text, fontWeight: '700' }}>👥 {t('Você está como convidado')}</Text>
          <Text style={{ color: C.sub, fontSize: 12 }}>{t('Aqui aparece, sem custo, o que os grupos compartilharam com você. Para usar a aba com os seus próprios dados, assine a aba.')}</Text>
          {SO_VER_CONVIDADO.includes(id) && <Text style={{ color: C.sub, fontSize: 12 }}>✨ {t('Você acompanha tudo o que foi compartilhado, sempre atualizado. Para incluir, editar e pegar tarefas, assine a aba.')}</Text>}
        </View>
      )}
      {!pai && !convidado && !!def.dica && !ctx.regs.length && <Text style={{ color: C.sub }}>{t(def.dica)}</Text>}
      {pai && colPai && <CabecalhoPai r={pai} c={colPai} ctx={ctx} onEditar={() => setFicha(pai)} onMais={() => opcoes(pai, colPai)} />}
      {pai && Topo && <Topo r={pai} ctx={ctx} pode={acessoDaFicha(pai).editar(pai.col)} irPara={(c, f) => {
        const alvo = def.colecoes.find(x => x.id === c); if (!alvo) return;
        setAbaSel(s => ({ ...s, [chave]: c })); setBusca('');
        setFiltro(f ? (alvo.filtros ?? []).findIndex(([nome]) => nome === f) : -1);
      }} />}
      {pai && colPai && !colPai.pai && !SEM_GRUPO.includes(colPai.id) && !!ROTULO_GRUPO[id] && !GRUPO_SO_DA_ABA.includes(id) && (colPai.grupoRecolhido
        ? <Recolhivel visual="cartao" key={'g' + pai.id} titulo={'👥 ' + t(colPai.nomeGrupo ?? ROTULO_GRUPO[id]!)}><GrupoDoItem semTitulo r={pai} c={colPai} ctx={ctx} onEnviar={() => enviarParaGrupo(pai, colPai)} /></Recolhivel>
        : <GrupoDoItem r={pai} c={colPai} ctx={ctx} onEnviar={() => enviarParaGrupo(pai, colPai)} />)}
      {!pai && !!ROTULO_GRUPO[id] && <GruposDaAba mod={id} cor={cor} />}
      {!pai && !!def.relatorio && <Pressable onPress={() => setRelatorio(true)} hitSlop={6} style={{ alignSelf: 'flex-start' }}>
        <Text style={{ color: cor, fontWeight: '700' }}>📊 {t('Relatório')} ›</Text></Pressable>}
      {!!def.relatorio && <def.relatorio visivel={relatorio} cor={cor} onFechar={() => setRelatorio(false)} />}

      {!semCol && <Recolhivel visual="cartao" key={'f' + (pai?.id ?? '')} inicial chave={colPai ? `aba:${id}:filhos:${colPai.id}` : undefined} desligado={!colPai?.filhosTitulo}
        titulo={colPai?.filhosTitulo ? `${t(colPai.filhosTitulo).toUpperCase()} (${doCol.length})` : ''}>
      {colsAqui.length > 1 && (
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 8 }}>
          {colsAqui.map(c => {
            const daCol = ctx.regs.filter(r => r.col === c.id && (c.pai ? r.pai === pai?.id : !r.pai));
            const n = daCol.length;
            // texto 19 (A4): andamento no nome da sub-aba ("Roteiro · 1 de 4 feitos")
            const pr = c.progresso && n ? c.progresso(daCol.filter(r => !org.arquivado(r.id)), ctx) : null;
            return <Chip key={c.id} cor={cor} on={c.id === col.id} label={`${typeof c.icone === 'string' ? c.icone + ' ' : ''}${t(c.nome)}${pr && pr.b ? ` · ${t(pr.rotulo, { a: pr.a, b: pr.b })}` : n ? ` (${n})` : ''}`}
              onPress={() => { setAbaSel(s => ({ ...s, [chave]: c.id })); setBusca(''); setFiltro(-1); }} />;
          })}
        </ScrollView>
      )}

      {!!resumo && <Text style={{ color: C.sub, fontSize: 13 }}>{resumo}</Text>}
      {!!col.topoLista && <col.topoLista ctx={ctx} pai={pai} lista={doCol} abrir={abrir} novo={c => novo(c)} pode={!pai || acessoDaFicha(pai).editar(pai.col)} />}
      {Painel && (Lista ? <Painel ctx={ctx} pai={pai} lista={doCol} abrir={abrir} novo={c => novo(c)} pode={!pai || acessoDaFicha(pai).editar(pai.col)} /> : (
        // B7 (0.65): resumos e painéis de apoio começam recolhidos
        <Recolhivel visual="cartao" key={'p' + col.id + (pai?.id ?? '')} chave={`aba:${id}:resumo:${col.id}`} titulo={'📊 ' + t('Resumo de {nome}', { nome: t(col.nome).toLowerCase() })}>
          <Painel ctx={ctx} pai={pai} lista={doCol} abrir={abrir} novo={c => novo(c)} pode={!pai || acessoDaFicha(pai).editar(pai.col)} />
        </Recolhivel>))}
      {/* texto 21 (B1): o cartão da lista inteira só em Professor (cobranças) e Consultório (recebimentos) */}
      {(listaComData(col) || !!col.aReceberNoPlanner) && !col.semEnviarLista && listaInteiraPermitida(id, col.id) && <EnviarAoPlanner aba={id} lista={col.id} cor={cor} aReceber={!!col.aReceberNoPlanner} />}

      {!!col.porFoto && podeCriar(id, col.id, col.pai ? pai : null) && (
        <Pressable onPress={() => novo({ _ia: 1 })} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, padding: 12, borderRadius: 14, backgroundColor: alpha(cor, 0.14), borderWidth: 1, borderColor: alpha(cor, 0.4) }}>
          <Text style={{ fontSize: 20 }}>📷</Text>
          <View style={{ flex: 1 }}>
            <Text style={{ color: C.text, fontWeight: '700' }}>{t(col.porFoto)}</Text>
            <Text style={{ color: C.sub, fontSize: 12 }}>{t('Tire a foto e a sua IA preenche a ficha')}</Text>
          </View>
        </Pressable>
      )}
      {doColTudo.length > 6 && !Lista && <Field value={busca} onChangeText={setBusca} placeholder={t('Buscar em {nome}', { nome: t(col.nome).toLowerCase() })} />}
      {(!!filtros.length || !!col.filtroArquivados) && !Lista && (
        <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
          <Chip small cor={cor} on={filtro === -1} label={col.filtroArquivados ? `${t('Todos')} (${naoArq.length})` : t('Todos')} onPress={() => setFiltro(-1)} />
          {filtros.map(([nome, f], i) => {
            const nf = naoArq.filter(r => !ehNota(r) && f(r, ctx));
            const n = col.pesoFiltro ? nf.reduce((s0, r) => s0 + col.pesoFiltro!(r), 0) : nf.length;
            return <Chip key={nome} small cor={cor} on={filtro === i} label={`${t(nome)}${n || col.contarZero ? ` (${n})` : ''}`} onPress={() => setFiltro(i)} />;
          })}
          {!!col.filtroArquivados && <Chip small cor={cor} on={verArq} label={`📦 ${t('Arquivados')}${sep.arquivados.length || doCol.length - naoArq.length ? ` (${doCol.length - naoArq.length})` : ''}`} onPress={() => setFiltro(-2)} />}
        </View>
      )}

      {Lista ? <Lista ctx={ctx} pai={pai} lista={doCol} abrir={abrir} novo={c => novo(c)} pode={!pai || acessoDaFicha(pai).editar(col.id)} /> : (<>
        {!doColTudo.length && !podeCriar(id, col.id, col.pai ? pai : null) && <Text style={{ color: C.mute }}>{t('Nenhum item em {nome}', { nome: t(col.nome).toLowerCase() })}</Text>}
        {!doColTudo.length && !extras.length && podeCriar(id, col.id, col.pai ? pai : null) && (
          <Pressable onPress={() => { if (!col.semNovo) novo(); }} style={{ backgroundColor: C.card, borderRadius: m.raio(16), padding: m.pad(16), gap: m.gap(6), borderWidth: 1, borderColor: C.line, borderStyle: 'dashed' }}>
            <Text style={{ color: C.text, fontWeight: '700' }}>{typeof col.icone === 'string' ? col.icone : '＋'} {col.vazioTitulo ? t(col.vazioTitulo) : t('Nenhum item em {nome}', { nome: t(col.nome).toLowerCase() })}</Text>
            {!!col.vazio && <Text style={{ color: C.sub, fontSize: 13 }}>{t(col.vazio)}</Text>}
            {!col.semNovo && <Text style={{ color: cor, fontWeight: '700' }}>+ {t('Adicionar {nome}', { nome: t(col.um).toLowerCase() })}</Text>}
          </Pressable>
        )}
        {!!(doColTudo.length || extras.length) && !lista.length && !separados.length && <Text style={{ color: C.sub }}>{t('Nada encontrado.')}</Text>}
        {(() => { cabecalhos.clear(); if (!col.grupoDe) return null;
          let ant: string | null = null;
          for (const r of lista) { const g = col.grupoDe(r); if (g !== ant) { cabecalhos.set(r.id, col.rotuloGrupo ? col.rotuloGrupo(g, lista.filter(x => col.grupoDe!(x) === g), ctx, pai) : g); ant = g; } }
          return null; })()}
        {sel.barra}
        <Ordenavel itens={col.grupoDe && col.gruposFechados && !q ? lista.filter(r => grupoAberto(col.grupoDe!(r)) || cabecalhos.has(r.id)) : lista} chave={r => r.id} desligado={!!q || filtro >= 0 || verArq || ordemPorData(col) || (!!extras.length && !col.reordenarIncluidos) || lista.some(r => !acessoDaFicha(r).editar(col.id))}
          onOrdem={l => (col.reordenarIncluidos ? col.reordenarIncluidos(l.map(r => r.id), ctx, pai) : ordenarRegs(id, l.map(r => r.id)))} selecao={sel.prop}
          grade={colunas > 1 && !col.grupoDe} estiloItem={colunas > 1 && !col.grupoDe ? () => larguraColuna(colunas, m.gap(8)) : undefined}
          render={r => {
            const cr = colDe(r);
            const pode = acessoDaFicha(r).editar(cr.id);
            const incluido = cr !== col && !!col.tirarIncluido;
            const cab = cabecalhos.get(r.id);
            const gk = col.grupoDe ? col.grupoDe(r) : '';
            const recolhe = !!col.grupoDe && !!col.gruposFechados && !q;
            const aberto = !recolhe || grupoAberto(gk);
            const cabTxt = !!cab && <Text style={{ color: C.sub, fontSize: 12, fontWeight: '800', letterSpacing: 0.4, flex: 1 }}>{recolhe ? (aberto ? '▾ ' : '▸ ') : ''}{cab.toUpperCase()}</Text>;
            const cabecalho = !!cab && (
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 6 }}>
                {recolhe ? <Pressable onPress={() => setAberto(chaveGrupo(gk), !aberto)} accessibilityRole="button" accessibilityState={{ expanded: aberto }} style={{ flex: 1, paddingVertical: 4 }}>{cabTxt}</Pressable> : cabTxt}
                {!!col.acaoGrupo && <Pressable onPress={() => col.acaoGrupo!.fn(gk, lista.filter(x => col.grupoDe!(x) === gk), ctx, pai)} hitSlop={8}>
                  <Text style={{ color: cor, fontSize: 12, fontWeight: '700' }}>{t(col.acaoGrupo.rotulo)}</Text></Pressable>}
              </View>);
            if (!aberto) return cabecalho || null;
            return (<>
              {cabecalho}
              <AcoesItem lembrar={{ aba: id, id: r.id, titulo: cr.titulo(r, ctx) || t(cr.um) }} nome={cr.titulo(r, ctx) || t(cr.um)} semConfirmar onExcluir={pode ? () => (incluido ? tirarDaqui(r, cr) : excluir(r, cr)) : undefined}
                onDuplicar={pode && !semDuplicarDe(r, cr, ctx) ? () => salvarReg(id, { ...novoReg(cr.id, r.pai, semTarefas(r.c)), itens: r.itens?.map(i => ({ ...i, id: uid(), done: false })) }) : undefined}
                onArquivar={incluido ? undefined : () => org.arquivar(r.id)} menuProprio fixar={{ on: org.fixado(r.id), alternar: () => org.alternarFixo(r.id) }}>
                <Cartao r={r} c={cr} ctx={ctx} destaque={r.id === item} onPress={() => abrir(r)} onMais={() => opcoes(r, cr)} onEditar={() => setFicha(regPorId(id, r.id) ?? r)} />
              </AcoesItem>
            </>);
          }} />
        {!!col.separado && <Separados titulo={t(col.separado.titulo)} icone={col.separado.icone} itens={separados}
          render={r => <Cartao r={r} c={colDe(r)} ctx={ctx} onPress={() => abrir(r)} onMais={() => opcoes(r, colDe(r))} onEditar={() => setFicha(regPorId(id, r.id) ?? r)} />} />}
        <Arquivados itens={arquivados} chave={r => r.id} cor={cor} render={r => { const cr = colDe(r); return <Text style={{ color: C.text }} numberOfLines={2}>{iconeDe(cr, r)} {cr.titulo(r, ctx) || t(cr.um)}</Text>; }}
          onRestaurar={r => org.restaurar(r.id)} onExcluir={r => excluir(r, colDe(r))} podeExcluir={r => acessoDaFicha(r).editar(colDe(r).id)} />
        {!!col.fimLista && <col.fimLista />}
        <View style={{ height: 90 }} />
      </>)}
      </Recolhivel>}
      {!pai && <RelatoriosGerados aba={id} cor={cor} />}
      {!pai && <ReceivedBoxes boxes={caixas} abaId={id} />}

      <FichaSheet reg={ficha} ctx={ctx} onClose={() => setFicha(null)} onExcluir={(r, c) => excluir(r, c)}
        onAbrir={x => { const r = ctx.porId(x); if (r) setTimeout(() => abrir(r), 350); }}
        onAgenda={(r, c) => { setFicha(null); setTimeout(() => colocarNaAgenda(r, c), 350); }} />
      <Sheet visible={perguntar} onClose={() => { marcarPerguntado(id); setPerguntar(false); }} title={t('Quais partes você vai usar?')}
        right={<Pressable onPress={() => { marcarPerguntado(id); setPerguntar(false); }}><Text style={{ color: cor, fontWeight: '800' }}>{t('Pronto')}</Text></Pressable>}>
        <ScrollView contentContainerStyle={{ padding: 16, gap: 10, paddingBottom: 60 }}>
          <Text style={{ color: C.sub, fontSize: 13 }}>{t('Desmarque o que você não usa: some da tela e deixa a aba mais simples. Dá para mudar depois em Ajustes › Abas › {aba} › Partes desta aba.', { aba: aba(id).nome })}</Text>
          <PartesDaAba mod={id} cor={cor} />
        </ScrollView>
      </Sheet>
      <ActionSheet title={menu?.t ?? ''} actions={menu?.a ?? null} onClose={() => setMenu(null)} />
    </Screen>
  );
}

/** Texto 13 B: linha recolhida no fim da lista (mesmo jeito dos Arquivados), com os cartões normais dentro. */
function Separados({ titulo, icone, itens, render }: { titulo: string; icone: string; itens: Reg[]; render: (r: Reg) => ReactNode }) {
  const [aberto, setAberto] = useState(false);
  if (!itens.length) return null;
  return (
    <View style={{ gap: 6, marginTop: 8 }}>
      <Pressable onPress={() => setAberto(a => !a)} hitSlop={6} accessibilityRole="button" accessibilityState={{ expanded: aberto }}>
        <Text style={{ color: C.sub, fontSize: 13 }}>{aberto ? '▾' : '▸'} {icone} {titulo} ({itens.length})</Text>
      </Pressable>
      {aberto && itens.map(r => <View key={r.id} style={{ opacity: 0.85 }}>{render(r)}</View>)}
    </View>
  );
}

function CabecalhoPai({ r, c, ctx, onEditar, onMais }: { r: Reg; c: Colecao; ctx: Ctx; onEditar: () => void; onMais: () => void }) {
  const linhas = c.campos.filter(f => f.t !== 'longo' && f.t !== 'cofre').map(f => [f, valorTxt(f, r.c[f.k], ctx, r.c)] as const).filter(([, v]) => v);
  const fotos = (r.anexos ?? []).filter(a => a.mime?.startsWith('image/') && fonteImagem(a)).slice(0, 4);
  return (
    <View style={{ backgroundColor: alpha(ctx.cor, 0.12), borderRadius: 16, padding: 14, gap: 6, borderWidth: 1, borderColor: alpha(ctx.cor, 0.35) }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
        <Text style={{ fontSize: 28 }}>{iconeDe(c, r)}</Text>
        <Text style={{ color: C.sub, flex: 1, fontSize: 13 }} numberOfLines={c.subCompleto ? undefined : 2}>{c.sub?.(r, ctx) ?? t(c.um)}</Text>
        <Pressable onPress={onEditar} hitSlop={8} style={{ paddingHorizontal: 10, paddingVertical: 6, borderRadius: 10, backgroundColor: C.card }}><Text style={{ color: C.text, fontSize: 12, fontWeight: '700' }}>{acessoDaFicha(r).editar(c.id) ? '✏️ ' + t('Editar') : '👁️ ' + t('Ver')}</Text></Pressable>
        <Pressable onPress={onMais} hitSlop={8} style={{ paddingHorizontal: 10, paddingVertical: 6, borderRadius: 10, backgroundColor: C.card }}><Text style={{ color: C.text, fontSize: 12, fontWeight: '800' }}>⋮</Text></Pressable>
      </View>
      <Contatos r={r} c={c} cor={ctx.cor} ctx={ctx} />
      {linhas.slice(0, 6).map(([f, v]) => <Text key={f.k} style={{ color: C.text, fontSize: 13 }}><Text style={{ color: C.sub }}>{f.rDe ? f.rDe(r.c) : t(f.r)}: </Text>{v}</Text>)}
      {!!fotos.length && <View style={{ flexDirection: 'row', gap: 6 }}>{fotos.map((a, i) => <Image key={i} source={{ uri: fonteImagem(a)! }} style={{ width: 56, height: 56, borderRadius: 8 }} />)}</View>}
    </View>
  );
}

/** Dentro do evento (ou da turma, da viagem, da obra...): o grupo dele, com convite e envio à mão. */
function GrupoDoItem({ r, c, ctx, onEnviar, semTitulo }: { r: Reg; c: Colecao; ctx: Ctx; onEnviar: () => void; semTitulo?: boolean }) {
  const { lista, erro } = useMeusGrupos();
  const [busy, setBusy] = useState(false);
  const g = lista?.find(x => x.id === r.g) ?? lista?.find(x => x.ref === r.id);
  const cor = ctx.cor;
  async function criar() {
    if (busy) return;
    setBusy(true);
    try {
      const g = await criarGrupoPara(ctx.mod, c.titulo(r, ctx) || t(c.um), r.id);
      // 0.29: o grupo da ficha já nasce com os dados compartilhados (quem entra vê e participa conforme a aba;
      // no Portal do Cliente o cliente vê só a pasta dele)
      await ligarCompartilhamento({ ...g, papeis: papeisDaAba(ctx.mod) }).catch(() => {});
      abrirGrupos({ aba: ctx.mod, ref: r.id });
    }
    catch (e: any) { Alert.alert(t('Não foi possível'), String(e?.message ?? e)); }
    finally { setBusy(false); }
  }
  if (lista === null) return null;
  if (!g) return (
    <Pressable onPress={criar} style={{ backgroundColor: C.card, borderRadius: 16, padding: 14, gap: 6, borderWidth: 1, borderColor: alpha(cor, 0.5), borderStyle: 'dashed' }}>
      {!semTitulo && <Text style={{ color: C.text, fontWeight: '800' }}>👥 {t(c.nomeGrupo ?? ROTULO_GRUPO[ctx.mod]!)}</Text>}
      <Text style={{ color: C.sub, fontSize: 13 }}>{c.textoGrupo
        // texto 13 G / 15 A: a regra dos grupos (quem entra acompanha; quem administra decide quem também inclui e edita)
        ? t(c.textoGrupo)
        : ehColaborativa(ctx.mod)
        ? t('Crie o grupo e mande o link: quem entrar vê tudo o que está aqui dentro e participa (inclui, edita, põe fotos). Só quem administra coloca ou tira pessoas.')
        : t('Crie o grupo e mande o link: quem entrar vê e participa conforme o papel que você escolher. Só quem administra coloca ou tira pessoas.')}</Text>
      {!!erro && <Text style={{ color: C.warn, fontSize: 12 }}>{erro}</Text>}
      {busy ? <ActivityIndicator color={cor} /> : <Text style={{ color: cor, fontWeight: '800' }}>+ {t('Criar o grupo e convidar')}</Text>}
    </Pressable>
  );
  return (
    <View style={{ backgroundColor: C.card, borderRadius: 16, padding: 12, gap: 10, borderWidth: 1, borderColor: alpha(cor, 0.5) }}>
      <Pressable onPress={() => abrirGrupos({ aba: ctx.mod, ref: r.id })} style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
        <Text style={{ fontSize: 24 }}>👥</Text>
        <View style={{ flex: 1 }}>
          <Text style={{ color: C.text, fontWeight: '800' }} numberOfLines={1}>{g.nome}</Text>
          <Text style={{ color: C.sub, fontSize: 12 }}>{[t('{n} pessoa(s)', { n: g.membros }), g.admin ? t('você administra') : ''].filter(Boolean).join(' · ')}</Text>
          {!!g.compartilhar && <Text style={{ color: cor, fontSize: 12, fontWeight: '700' }}>📂 {g.admin ? t('Dados compartilhados com o grupo') : t('Dados compartilhados · seu papel: {p}', { p: t(papeisDoGrupo(g)[g.papel]?.nome ?? N_('Só acompanha, sem valores')) })}</Text>}
        </View>
        <Text style={{ color: C.mute, fontSize: 20 }}>›</Text>
      </Pressable>
      <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
        {g.admin && !g.compartilhar && <Chip small cor={cor} on label={'📂 ' + t('Compartilhar os dados')} onPress={() => confirmarCompartilhar(g)} />}
        {g.admin && <Chip small cor={cor} on={!!g.compartilhar} label={'📤 ' + t('Convidar')} onPress={() => convidarPara(g)} />}
        {g.admin && <Chip small cor={cor} on={false} label="💬 WhatsApp" onPress={() => convidarPara(g, true)} />}
        {(g.admin || g.membros_enviam) && <Chip small cor={cor} on={false} label={'📢 ' + t('Enviar ao grupo')} onPress={onEnviar} />}
        <Chip small cor={cor} on={false} label={'👤 ' + t('Membros')} onPress={() => abrirGrupos({ aba: ctx.mod, ref: r.id })} />
      </View>
    </View>
  );
}

/** Na página inicial da aba (B8, 0.65): uma linha pequena e discreta, recolhida; um toque abre os grupos. */
function GruposDaAba({ mod, cor }: { mod: ModId; cor: string }) {
  const { lista } = useMeusGrupos();
  const [aberto, setAberto] = useState(false);
  const n0 = (lista ?? []).filter(g => g.aba === mod).length;
  if (!aberto) return (
    <Pressable onPress={() => setAberto(true)} hitSlop={6} style={{ flexDirection: 'row', alignItems: 'center', gap: 6, paddingVertical: 2 }}>
      <Text style={{ color: C.sub, fontSize: 12 }}>👥 {n0 ? t('Grupos desta aba: {n}', { n: n0 }) : t('Grupos e convites')} ▸</Text>
    </Pressable>
  );
  return <GruposDaAbaAberto mod={mod} cor={cor} />;
}
function GruposDaAbaAberto({ mod, cor }: { mod: ModId; cor: string }) {
  const { lista } = useMeusGrupos();
  const n = (lista ?? []).filter(g => g.aba === mod).length;
  const equipe = (lista ?? []).find(g => g.aba === mod && !g.ref);
  if (equipe && GRUPO_SO_DA_ABA.includes(mod)) return (
    <View style={{ backgroundColor: alpha(cor, 0.1), borderRadius: 14, padding: 12, gap: 8 }}>
      <Pressable onPress={() => abrirGrupos({ aba: mod })} style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
        <Text style={{ fontSize: 18 }}>👥</Text>
        <View style={{ flex: 1 }}>
          <Text style={{ color: C.text, fontWeight: '700' }} numberOfLines={1}>{equipe.nome} · {t('{n} pessoa(s)', { n: equipe.membros })}</Text>
          <Text style={{ color: equipe.compartilhar ? cor : C.sub, fontSize: 12 }}>{equipe.compartilhar
            ? (equipe.admin ? t('A equipe vê e altera conforme o papel de cada um') : t('Seu papel: {p}', { p: t(papeisDoGrupo(equipe)[equipe.papel]?.nome ?? N_('Só acompanha, sem valores')) }))
            : t('Os dados da aba ainda são só seus')}</Text>
        </View>
        <Text style={{ color: cor, fontWeight: '800' }}>›</Text>
      </Pressable>
      {equipe.admin && !equipe.compartilhar && <Chip small cor={cor} on label={'📂 ' + t('Compartilhar os dados com a equipe')} onPress={() => confirmarCompartilhar(equipe)} />}
    </View>
  );
  return (
    <Pressable onPress={() => abrirGrupos({ aba: mod })} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: alpha(cor, 0.1), borderRadius: 14, paddingVertical: 10, paddingHorizontal: 12 }}>
      <Text style={{ fontSize: 18 }}>👥</Text>
      <Text style={{ color: C.text, flex: 1, fontWeight: '600' }} numberOfLines={1}>
        {n ? t('Grupos desta aba: {n}', { n }) : t('Criar grupo e convidar pessoas')}
      </Text>
      <Text style={{ color: cor, fontWeight: '800' }}>›</Text>
    </Pressable>
  );
}

function Cartao({ r, c, ctx, destaque, onPress, onMais, onEditar }: { r: Reg; c: Colecao; ctx: Ctx; destaque?: boolean; onPress: () => void; onMais: () => void; onEditar?: () => void }) {
  const feito = c.feito?.(r) ?? false;
  const cont = c.data && !feito ? contagem(r.c[c.data]) : null;
  const itens = r.itens ?? [];
  const foto = (r.anexos ?? []).find(a => a.mime?.startsWith('image/') && fonteImagem(a));
  const rapido = c.rapido ? c.campos.find(f => f.k === c.rapido) : null;
  const naAgenda = !!r.tarefa;
  const grupos = useMeusGrupos().lista;
  const grupo = !c.pai ? grupos?.find(g => g.ref === r.id) : undefined;
  // texto 19 (A6): em Viagens, com 1 pessoa só no grupo, não mostra o "✍️ nome"
  const gDaFicha = r.g ? grupos?.find(g => g.id === r.g) : undefined;
  const sozinhoNoGrupo = (ctx.mod === 'viagens' || ctx.mod === 'eventos') && !!gDaFicha && gDaFicha.membros < 2;   // texto 23 (A): também em Eventos
  const [lista, setLista] = useState(false);   // checklist aberta no próprio cartão (mala, compras...)
  const [situacao, setSituacao] = useState(false);
  const [pagar, setPagar] = useState(false);   // 0.69 (B2): janela do valor pago ou recebido
  const [perguntaValor, setPerguntaValor] = useState(false);   // texto 23 (E7): marcou "comprado": quanto custou (pode pular)
  const pode = acessoDaFicha(r).editar(c.id);   // ficha compartilhada em que o meu papel só deixa ver
  const m = useCaixas();   // "Tamanho das caixas" (Grande = como antes)
  const marcar = (id: string) => pode && mudarReg(ctx.mod, r.id, x => ({ ...x, itens: (x.itens ?? []).map(i => (i.id === id ? { ...i, done: !i.done } : i)) }));
  return (
    <Pressable onPress={onPress} style={({ pressed }) => ({ backgroundColor: C.card, borderRadius: m.raio(16), ...m.pads(12), gap: m.gap(6), opacity: pressed ? 0.85 : feito ? 0.55 : 1,
      borderWidth: 1, borderColor: destaque ? ctx.cor : C.line })}>
      <View style={{ flexDirection: 'row', gap: 10, alignItems: 'center' }}>
        {!!c.check && (
          <Pressable disabled={!pode} onPress={() => { const k = c.check!; if (c.pagamento && !r.c[k]) return setPagar(true); const ligou = !r.c[k]; mudarReg(ctx.mod, r.id, x => { const n = comDataAoLigar(c, { ...x.c, [k]: !x.c[k] }, k, x.c[k]); return { ...x, c: c.ajustar ? c.ajustar(n, k, ctx, ctx.porId(x.pai)) : n }; }); if (c.id === 'reserva' || (ctx.mod === 'eventos' && c.aoSalvar)) { const nr = regPorId(ctx.mod, r.id); if (nr) c.aoSalvar?.(nr, ctx); } if (ligou && c.aoMarcar) setPerguntaValor(true); }} hitSlop={10} accessibilityRole="checkbox" accessibilityState={{ checked: !!r.c[c.check] }}
            style={{ width: m.v(28, 26, 22), height: m.v(28, 26, 22), borderRadius: m.v(8, 7, 6), borderWidth: 2, borderColor: r.c[c.check] ? ctx.cor : C.mute, backgroundColor: r.c[c.check] ? ctx.cor : 'transparent', alignItems: 'center', justifyContent: 'center' }}>
            {!!r.c[c.check] && <Text style={{ color: '#fff', fontWeight: '900' }}>✓</Text>}
          </Pressable>
        )}
        {foto ? <Image source={{ uri: fonteImagem(foto)! }} style={{ width: m.alt(44), height: m.alt(44), borderRadius: m.raio(10) }} /> : <Text style={{ fontSize: m.icone(26), width: m.alt(36), textAlign: 'center' }}>{iconeDe(c, r)}</Text>}
        <View style={{ flex: 1, gap: 2 }}>
          <Text style={{ color: C.text, fontWeight: '700', fontSize: 15, textDecorationLine: feito ? 'line-through' : 'none' }} numberOfLines={2}>{c.titulo(r, ctx) || t(c.um)}</Text>
          {!!c.sub && !!c.sub(r, ctx) && <Text style={{ color: C.sub, fontSize: 12 }} numberOfLines={2}>{c.sub(r, ctx)}</Text>}
          {!!c.extraCartao && <c.extraCartao r={r} ctx={ctx} pode={pode} />}
          {m.resumo && (!ehNota(r) || !!String(r.c.titulo ?? '').trim()) && typeof r.c?.[c.chaveAnotacoes ?? 'notas'] === 'string' && !!r.c[c.chaveAnotacoes ?? 'notas'].trim() && <TextoMais texto={r.c[c.chaveAnotacoes ?? 'notas'].trim()} style={{ fontSize: 12, color: C.mute }} />}
        </View>
        <Pressable onPress={onMais} hitSlop={10} accessibilityLabel={t('Mais opções')} style={{ padding: 4 }}><Text style={{ color: C.sub, fontSize: 18, fontWeight: '800' }}>⋮</Text></Pressable>
      </View>
      {(!!cont || !!itens.length || !!r.anexos?.length || naAgenda || !!grupo || (!!r.g && !!r.por && !sozinhoNoGrupo)) && (
        <View style={{ flexDirection: 'row', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          {!!cont && <Text style={{ color: cont.cor, fontWeight: '700', fontSize: 12 }}>📅 {dataBr(r.c[c.data!])} · {cont.txt}</Text>}
          {!!itens.length && <Pressable onPress={() => setLista(x => !x)} hitSlop={8}><Text style={{ color: ctx.cor, fontSize: 12, fontWeight: '700' }}>☑ {itens.filter(i => i.done).length}/{itens.length} {lista ? '▴' : '▾'}</Text></Pressable>}
          {!!r.anexos?.length && <Text style={{ color: C.sub, fontSize: 12 }}>{iconeAnexo(r.anexos[0])} {r.anexos.length}</Text>}
          {naAgenda && <Text style={{ color: C.sub, fontSize: 12 }}>🗓️ {t('no Planner')}</Text>}
          {!!grupo && <Text style={{ color: C.sub, fontSize: 12 }}>👥 {grupo.membros}</Text>}
          {!!r.g && !!r.por && !sozinhoNoGrupo && <Text style={{ color: C.sub, fontSize: 12 }}>✍️ {r.por}</Text>}
        </View>
      )}
      <Contatos r={r} c={c} cor={ctx.cor} ctx={ctx} />
      {lista && itens.map(i => (
        <Pressable key={i.id} onPress={() => marcar(i.id)} style={{ flexDirection: 'row', gap: 10, alignItems: 'center', paddingVertical: m.pad(4) }}>
          <Text style={{ fontSize: 17, color: i.done ? ctx.cor : C.sub }}>{i.done ? '☑' : '☐'}</Text>
          <Text style={{ color: i.done ? C.mute : C.text, flex: 1, textDecorationLine: i.done ? 'line-through' : 'none' }}>{i.text}</Text>
        </Pressable>
      ))}
      {/* 0.65 (B9): a situação é um botão só; tocar abre as opções */}
      {rapido?.op && (
        <View style={{ flexDirection: 'row' }}>
          <Pressable disabled={!pode} onPress={() => setSituacao(true)} style={{ flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 12, paddingVertical: m.pad(6), borderRadius: 16, backgroundColor: r.c[rapido.k] ? ctx.cor : C.card2 }}>
            <Text style={{ color: r.c[rapido.k] ? '#fff' : C.text, fontSize: 12, fontWeight: '700' }}>{r.c[rapido.k] ? t(r.c[rapido.k]) : t(rapido.r)}</Text>
            {pode && <Text style={{ color: r.c[rapido.k] ? '#fff' : C.sub, fontSize: 11 }}>▾</Text>}
          </Pressable>
          <ActionSheet title={t(rapido.r)} actions={situacao ? (rapido.opDe ? rapido.opDe(ctx, ctx.porId(r.pai), r.c) : rapido.op ?? []).map(o => ({ label: (r.c[rapido.k] === o ? '✓ ' : '') + t(o), onPress: () => {
            // texto 15 C4: a folha continua igual; só acrescenta as datas da situação e, no "Parei", abre a ficha para o motivo
            const extra = r.c[rapido.k] === o ? undefined : c.aoRapido?.(r, o);
            const datas = r.c[rapido.k] !== o && c.situacao?.campo === rapido.k ? c.situacao.datas(r.c, o) : {};
            mudarCampos(ctx.mod, r.id, { [rapido.k]: o, ...datas, ...(extra?.mudar ?? {}) });
            if (extra?.abrir && onEditar) setTimeout(onEditar, 450);
          } })) : null} onClose={() => setSituacao(false)} />
        </View>
      )}
      {perguntaValor && !!c.aoMarcar && <PerguntaValor titulo={t(c.aoMarcar.titulo)} dica={c.aoMarcar.dica ? t(c.aoMarcar.dica) : undefined} moeda={ctx.moeda} cor={ctx.cor}
        onFechar={v => { setPerguntaValor(false); const nr = regPorId(ctx.mod, r.id); if (nr) c.aoMarcar!.ok(nr, ctx, v); }} />}
      {pagar && !!c.pagamento && <ValorPago item={itemPago(r, c, ctx)} cor={ctx.cor} moeda={moedaDo(r.c, c.pagamento.valor ?? 'valor', ctx.moeda)} onFechar={() => setPagar(false)} />}
    </Pressable>
  );
}

/** Texto 23 (E7): ao marcar a caixinha, pergunta um valor ("Quanto custou?"); "Pular" marca sem valor. */
function PerguntaValor({ titulo, dica, moeda, cor, onFechar }: { titulo: string; dica?: string; moeda: string; cor: string; onFechar: (v: number | null) => void }) {
  const [txt, setTxt] = useState('');
  const v = lerValor(txt), ok = isFinite(v) && v > 0;
  return (
    <Modal transparent animationType="fade" onRequestClose={() => onFechar(null)}>
      <Pressable onPress={() => onFechar(null)} style={{ flex: 1, backgroundColor: C.overlay, justifyContent: 'center', padding: 24 }}>
        <Pressable style={{ backgroundColor: C.card, borderRadius: 18, padding: 16, gap: 12, alignSelf: 'center', width: '100%', maxWidth: 380 }}>
          <Text style={{ color: C.text, fontWeight: '800', fontSize: 16 }}>{titulo}</Text>
          {!!dica && <Text style={{ color: C.sub, fontSize: 12 }}>{dica}</Text>}
          <TextInput value={txt} onChangeText={setTxt} keyboardType="decimal-pad" autoFocus inputAccessoryViewButtonLabel={t('Concluído')} placeholder={dinheiro(0, moeda)} placeholderTextColor={C.mute}
            accessibilityLabel={titulo} style={{ backgroundColor: C.card2, borderRadius: 10, paddingHorizontal: 12, paddingVertical: 10, color: C.text, fontSize: 18, fontWeight: '700' }} />
          <View style={{ flexDirection: 'row', gap: 16, justifyContent: 'flex-end', alignItems: 'center' }}>
            <Pressable onPress={() => onFechar(null)} hitSlop={8}><Text style={{ color: C.sub, fontWeight: '700' }}>{t('Pular')}</Text></Pressable>
            <Pressable disabled={!ok} onPress={() => onFechar(Math.round(v * 100) / 100)} hitSlop={8} style={{ paddingHorizontal: 18, paddingVertical: 8, borderRadius: 10, backgroundColor: ok ? cor : C.card2 }}>
              <Text style={{ color: ok ? '#fff' : C.mute, fontWeight: '800' }}>{t('Lançar o gasto')}</Text>
            </Pressable>
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

/** A primeira data com aviso preenchida na ficha (vacina, IPVA, devolução...). */
const dataComAviso = (c: Colecao, r: Reg) => c.campos.filter(f => f.t === 'data' && !!f.aviso?.length).map(f => String(r.c[f.k] ?? '')).find(Boolean) ?? '';

/** Texto 19 (A3/G1c): o campo sim/não que guarda quando foi ligado (data da conclusão, "visitado em"). */
function comDataAoLigar(c: Colecao, n: Record<string, any>, k: string, antes: unknown): Record<string, any> {
  const f = c.campos.find(x => x.k === k);
  if (!f?.dataAoLigar || !!n[k] === !!antes) return n;
  return { ...n, [f.dataAoLigar]: n[k] ? n[f.dataAoLigar] || today() : '' };
}

/** 0.69 (B2): a caixinha de um valor a pagar ou a receber abre a mesma janela de Finanças (valor pago e a diferença + ou −).
 *  Ao confirmar, marca como pago (com os padrões da coleção, ex.: data de hoje e PIX) e guarda o valor pago de fato. */
function itemPago(r: Reg, c: Colecao, ctx: Ctx): ComponentProps<typeof ValorPago>['item'] {
  const pg = c.pagamento!, k = c.check!;
  const venc = String(r.c[pg.venc ?? c.data ?? ''] || '') || today();
  const marcar = (v?: number, data?: string, forma?: string) => {
    mudarReg(ctx.mod, r.id, x => {
      const n0 = { ...x.c, [k]: true };
      const n = c.ajustar ? c.ajustar(n0, k, ctx, ctx.porId(x.pai)) : n0;
      if (pg.valorPago && v != null) n[pg.valorPago] = v;
      if (pg.data) n[pg.data] = data || n[pg.data] || today();
      if (pg.forma && forma) n[pg.forma] = forma;
      return { ...x, c: n };
    });
    // texto 19 (G2d): o que a ficha faz ao salvar vale também ao marcar pago pela caixinha (ex.: a reserva paga vira gasto)
    const nr = regPorId(ctx.mod, r.id); if (nr) c.aoSalvar?.(nr, ctx);
  };
  return { chave: r.id, aba: ctx.mod, id: r.id, icone: iconeDe(c, r), titulo: c.titulo(r, ctx) || t(c.um), detalhe: '', data: venc,
    valor: num(r.c[pg.valor ?? 'valor']), tipo: typeof pg.tipo === 'function' ? pg.tipo(r, ctx) : pg.tipo, marcar,
    ...(pg.forma || pg.data ? { formas: pg.forma ? (c.campos.find(f => f.k === pg.forma)?.op ?? []) : [], formaPadrao: pg.forma ? String(r.c[pg.forma] ?? '') || undefined : undefined } : {}) };
}

// ─── Ficha de edição ─────────────────────────────────────────────────────────────────────────────
/** 0.29: quem incluiu a ficha (ou o administrador) escolhe quem do grupo não vê. Vale para tudo o que está dentro dela. */
function QuemVe({ reg, mod, cor }: { reg: Reg; mod: ModId; cor: string }) {
  const [membros, setMembros] = useState<Membro[] | null>(null);
  const [eu, setEu] = useState('');
  const [aberto, setAberto] = useState(false);
  const [oc, setOc] = useState<string[]>(regPorId(mod, reg.id)?.oc ?? reg.oc ?? []);
  const g = grupoPorId(reg.g);
  useEffect(() => { meuId().then(setEu).catch(() => {}); }, []);
  useEffect(() => { if (aberto && !membros && reg.g) membrosDe(reg.g).then(setMembros).catch(() => setMembros([])); }, [aberto]); // eslint-disable-line react-hooks/exhaustive-deps
  const pode = !!g && (g.admin || !reg.de || reg.de === eu);
  const nomes = (membros ?? []).filter(m => oc.includes(m.user_id)).map(m => m.nome || '?');
  const alternar = (id: string) => {
    const n = oc.includes(id) ? oc.filter(x => x !== id) : [...oc, id];
    setOc(n);
    ocultarDe(mod, { ...reg, oc: n }, n);
  };
  return (
    <View style={{ gap: 6, marginTop: 4 }}>
      <Pressable disabled={!pode} onPress={() => setAberto(x => !x)} hitSlop={6}>
        <Text style={{ color: oc.length ? C.warn : C.sub, fontSize: 12, fontWeight: '700' }}>
          {oc.length ? '🙈 ' + (nomes.length ? t('Escondido de: {n}', { n: nomes.join(', ') }) : t('Escondido de {n} pessoa(s)', { n: oc.length })) : '👁️ ' + t('Todos do grupo veem')}{pode ? ' ▾' : ''}</Text>
      </Pressable>
      {aberto && (
        <View style={{ gap: 6 }}>
          <Text style={{ color: C.sub, fontSize: 12 }}>{t('Toque em quem NÃO deve ver esta ficha (e o que está dentro dela). Ex.: o presente surpresa.')}</Text>
          {membros === null ? <ActivityIndicator color={cor} /> : (
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
              {membros.filter(m => m.user_id !== eu && m.user_id !== reg.de).map(m => (
                <Chip key={m.user_id} small cor={C.warn} on={oc.includes(m.user_id)} label={(oc.includes(m.user_id) ? '🙈 ' : '') + (m.nome || '?')} onPress={() => alternar(m.user_id)} />
              ))}
              {!membros.filter(m => m.user_id !== eu).length && <Text style={{ color: C.mute, fontSize: 12 }}>{t('Ainda não há outras pessoas no grupo (ou você não vê a lista de membros).')}</Text>}
            </View>
          )}
        </View>
      )}
    </View>
  );
}

/** Texto 19 (B1): lista de pessoas: as que entram sozinhas (👥, sem ✕) e as avulsas, com "+ Adicionar pessoa".
 *  Guarda como texto ("Ana, Bia"), o mesmo formato de antes (relatórios e versões antigas continuam lendo). */
function CampoNomes({ valor, auto, cor, onMudar, botao, ph, sugeridos }: { valor: unknown; auto: string[]; cor: string; onMudar: (v: string) => void; botao?: string; ph?: string; sugeridos?: string[] }) {
  const [novo, setNovo] = useState('');
  const lista = (Array.isArray(valor) ? valor.map(String) : String(valor ?? '').split(/,|;|\/|\s+e\s+|\s+and\s+|\s+y\s+|&|\n/i)).map(x => x.trim()).filter(Boolean);
  const avulsos = [...new Map(lista.map(x => [norm(x), x])).values()].filter(x => !auto.some(a => norm(a) === norm(x)));
  const add = () => { const n = novo.trim(); if (!n) return; if (![...auto, ...avulsos].some(x => norm(x) === norm(n))) onMudar([...avulsos, n].join(', ')); setNovo(''); };
  return (<>
    <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
      {auto.map(n => <View key={'a' + n} style={{ paddingHorizontal: 12, paddingVertical: 7, borderRadius: 16, backgroundColor: alpha(cor, 0.16) }}><Text style={{ color: C.text, fontSize: 13 }}>👥 {n}</Text></View>)}
      {avulsos.map(n => (
        <Pressable key={'v' + n} onPress={() => onMudar(avulsos.filter(x => x !== n).join(', '))} accessibilityLabel={t('Tirar {nome}', { nome: n })}
          style={{ flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 12, paddingVertical: 7, borderRadius: 16, backgroundColor: C.card2 }}>
          <Text style={{ color: C.text, fontSize: 13 }}>{n}</Text><Text style={{ color: C.mute, fontSize: 12 }}>✕</Text>
        </Pressable>))}
    </View>
    <View style={{ flexDirection: 'row', gap: 6 }}>
      <Field value={novo} onChangeText={setNovo} placeholder={ph ?? t('Nome')} style={{ flex: 1, minWidth: 0 }} onSubmitEditing={add} blurOnSubmit={false} returnKeyType="done" />
      <Chip cor={cor} on={false} label={'+ ' + (botao ?? t('Adicionar pessoa'))} onPress={add} />
    </View>
    {/* texto 25: as etiquetas já usadas nas outras fichas, para escolher com um toque */}
    {!!sugeridos?.length && <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
      {sugeridos.filter(x => !avulsos.some(a => norm(a) === norm(x))).map(x => <Chip key={'s' + x} small cor={cor} on={false} label={'+ ' + x} onPress={() => onMudar([...avulsos, x].join(', '))} />)}
    </View>}
  </>);
}

/** Texto 25 (C4/F1): CPF ou CNPJ. Já preenchido, aparece mascarado; ver ou mudar pede a senha do app. */
function CampoDoc({ valor, cor, empresa, novo, onMudar }: { valor: unknown; cor: string; empresa: boolean; novo: boolean; onMudar: (v: string) => void }) {
  const [aberto, setAberto] = useState(novo);
  const ident = useConfirmarIdentidade(cor);
  if (!aberto) return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
      <Text style={{ color: C.text, fontSize: 16, flex: 1 }}>{mascararDoc(valor)}</Text>
      <Chip small cor={cor} on={false} label={'👁️ ' + t('Ver ou mudar')} onPress={async () => { if (await ident.pedir(t('Ver o documento completo'))) setAberto(true); }} />
      {ident.tela}
    </View>);
  return <Field value={String(valor ?? '')} onChangeText={x => onMudar(formatarDoc(x))} keyboardType="number-pad" placeholder={empresa ? '00.000.000/0000-00' : '000.000.000-00'} />;
}

/** Texto 25 (B3): CEP com "Buscar" (preenche rua, bairro, cidade e estado). */
function CampoCep({ valor, cor, onMudar, onAchar }: { valor: unknown; cor: string; onMudar: (v: string) => void; onAchar: (e: { rua?: string; bairro?: string; cidade?: string; uf?: string }) => void }) {
  const [busy, setBusy] = useState(false);
  const d = String(valor ?? '').replace(/\D/g, '');
  const buscar = async () => {
    if (d.length !== 8) return Alert.alert(t('CEP incompleto'), t('O CEP tem 8 números.'));
    setBusy(true);
    try {
      const r = await (await fetch(`https://viacep.com.br/ws/${d}/json/`)).json();
      if (r?.erro) Alert.alert(t('CEP não encontrado'), t('Confira os números ou preencha o endereço à mão.'));
      else onAchar({ rua: r.logradouro, bairro: r.bairro, cidade: r.localidade, uf: r.uf });
    } catch { Alert.alert(t('Não foi possível buscar o CEP'), t('Confira a internet ou preencha o endereço à mão.')); }
    finally { setBusy(false); }
  };
  return (
    <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
      <Field value={String(valor ?? '')} onChangeText={x => { const n = x.replace(/\D/g, '').slice(0, 8); onMudar(n.length > 5 ? n.slice(0, 5) + '-' + n.slice(5) : n); }}
        keyboardType="number-pad" placeholder="00000-000" style={{ flex: 1 }} />
      <Chip cor={cor} on={false} label={busy ? '...' : '🔎 ' + t('Buscar')} onPress={buscar} />
    </View>);
}

function FichaSheet({ reg, ctx, onClose, onExcluir, onAgenda, onAbrir }: { reg: Reg | null; ctx: Ctx; onClose: () => void;
  onExcluir: (r: Reg, c: Colecao) => void; onAgenda: (r: Reg, c: Colecao) => void; onAbrir?: (id: string) => void }) {
  const col = reg ? colecaoDe(ctx.mod, reg.col) : null;
  return (
    <Sheet visible={!!reg && !!col} onClose={onClose} title={reg && col ? (ctx.regs.some(x => x.id === reg.id) ? (!acessoDaFicha(reg).editar(col.id) ? t('Ver {nome}', { nome: t(col.um).toLowerCase() })
      : col.tituloEditar ? t(col.tituloEditar) : t('Editar {nome}', { nome: t(col.um).toLowerCase() })) : col.tituloNovo ? t(col.tituloNovo) : t('Novo: {nome}', { nome: t(col.um).toLowerCase() })) : ''}>
      {reg && col && <Ficha key={reg.id} reg={reg} col={col} ctx={ctx} onClose={onClose} onExcluir={onExcluir} onAgenda={onAgenda} onAbrir={onAbrir} />}
    </Sheet>
  );
}

function Ficha({ reg, col, ctx, onClose, onExcluir, onAgenda, onAbrir }: { reg: Reg; col: Colecao; ctx: Ctx; onClose: () => void;
  onExcluir: (r: Reg, c: Colecao) => void; onAgenda: (r: Reg, c: Colecao) => void; onAbrir?: (id: string) => void }) {
  const cor = ctx.cor;
  const existe = ctx.regs.some(x => x.id === reg.id);
  const cofre = useCofre();
  const cofreSheet = useCofreSheet(cor);
  // ficha compartilhada num grupo (0.25): o que o meu papel deixa ver e alterar
  const paiReg = ctx.porId(reg.pai);
  const ac = acessoDaFicha(existe ? reg : reg.g ? reg : paiReg);
  const compart = !!reg.g || !!paiReg?.g || (!reg.pai && !!grupoDaAba(ctx.mod));
  const soVer = existe && !ac.editar(col.id);
  const gruposF = useMeusGrupos().lista;
  const gF = (reg.g ?? paiReg?.g) ? gruposF?.find(g => g.id === (reg.g ?? paiReg?.g)) : undefined;
  const sozinhoNoGrupo = (ctx.mod === 'viagens' || ctx.mod === 'eventos') && !!gF && gF.membros < 2;   // texto 19 (A6) e 23 (A)
  // no grupo os campos do cofre ficam abertos para quem tem acesso (o cofre é pessoal); fora do grupo, criptografados
  const camposCofre = compart ? [] : col.campos.filter(f => f.t === 'cofre');
  // fotos e arquivos protegidos: fichas com campos do cofre e tudo da aba Saúde (receitas, laudos...)
  const protegido = !compart && (camposCofre.length > 0 || ctx.mod === 'saude');
  // campos do cofre ficam abertos só enquanto a ficha está na tela
  const abrirC = (c: Record<string, any>) => {
    const o = { ...c };
    for (const f of col.campos) if (f.t === 'cofre' && cifrado(o[f.k])) { const v = decifrar(o[f.k]); if (v !== null) o[f.k] = v; }
    return o;
  };
  const [c, setC] = useState<Record<string, any>>(() => abrirC(reg.c));
  const [anexos, setAnexos] = useState(reg.anexos ?? []);
  const [itens, setItens] = useState(reg.itens ?? []);
  const [novoItem, setNovoItem] = useState('');
  const [outroTxt, setOutroTxt] = useState<Record<string, string>>({});   // texto 13 A: caixa do "Outro" aberta (campo → texto)
  const [picker, setPicker] = useState<{ k: string; mode: 'date' | 'time' } | null>(null);
  const [fuso, setFuso] = useState<string | null>(null);
  const [moedaCampo, setMoedaCampo] = useState<string | null>(null);   // 0.69 (B1): escolhendo a moeda deste valor
  const comMoeda = (!!col.pagamento && !col.pagamento.umaMoeda) || !!col.moedaPorValor;
  /** Guarda a moeda só quando é diferente da que o valor já teria (a do valor principal ou a do app). */
  const mudarMoeda = (k: string, m: string) => setC(x => {
    const outras = { ...(x.moedas ?? {}) }; delete outras[k];
    if (m !== moedaDo({ moedas: outras }, k, ctx.moeda)) outras[k] = m;
    return { ...x, moedas: outras };
  });
  const [txt, setTxt] = useState<Record<string, string>>(() => Object.fromEntries(col.campos.filter(f => f.t === 'numero' || f.t === 'dinheiro').map(f => [f.k, reg.c[f.k] === '' || reg.c[f.k] == null ? '' : String(reg.c[f.k]).replace('.', ',')])));
  useEffect(() => { if (cofre.aberto) setC(abrirC); }, [cofre.aberto]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k: string, v: any) => setC(x => {
    let n = col.ajustar ? col.ajustar({ ...x, [k]: v }, k, ctx, paiReg) : { ...x, [k]: v };
    n = comDataAoLigar(col, n, k, x[k]);
    // texto 16 A7: a situação mudou: a data dela entra sozinha (se estiver vazia) e o app guarda quando mudou
    if (col.situacao && k === col.situacao.campo && v && v !== x[k]) n = { ...n, ...col.situacao.datas(x, v) };
    // números calculados pelo ajuste (ex.: próxima troca de óleo) aparecem no campo
    const muda = col.campos.filter(f => (f.t === 'numero' || f.t === 'dinheiro') && f.k !== k && n[f.k] !== x[f.k]);
    if (muda.length) setTxt(s => ({ ...s, ...Object.fromEntries(muda.map(f => [f.k, n[f.k] === '' || n[f.k] == null ? '' : String(n[f.k]).replace('.', ',')])) }));
    return n;
  });
  const fechado = (k: string) => cifrado(c[k]);

  async function salvar(semPergunta?: unknown) {
    // 0.70 (E1): pergunta que a pessoa precisa responder antes de salvar (ex.: o alarme do remédio no silencioso)
    const falta = col.campos.find(f => f.obrigatorio && (!existe || !!reg.c[f.k]) && (!f.mostrar || f.mostrar(c, ctx, paiReg))
      && (c[f.k] === '' || c[f.k] == null || (Array.isArray(c[f.k]) && !c[f.k].length)));
    if (falta) { Alert.alert(t('Falta uma resposta'), t(falta.obrigatorio!)); return; }
    // texto 15 C2: ano precisa ser um ano válido (4 dígitos, de 1870 até daqui a 10 anos)
    const anoRuim = col.campos.find(f => f.ano && String(txt[f.k] ?? '').trim() && !(num(txt[f.k]) >= 1870 && num(txt[f.k]) <= Number(today().slice(0, 4)) + 10));
    if (anoRuim) { Alert.alert(t('Ano inválido'), t('Digite o ano com 4 números (ex.: {a}).', { a: today().slice(0, 4) })); return; }
    // texto 25 (E5): aviso de duplicado (ou outra conferência da coleção) antes de salvar
    if (col.antesDeSalvar) {
      const ok = await col.antesDeSalvar({ ...reg, c }, ctx, !existe);
      if (ok === false) return;
      if (typeof ok === 'string' && ok.startsWith('abrir:')) { onClose(); onAbrir?.(ok.slice(6)); return; }
    }
    const final: Record<string, any> = { ...c };
    // texto 27 (sobras): o que mudou fora do formulário enquanto a ficha estava aberta (ex.: a conversa sobre o documento,
    // o "visto pelo cliente", o "Li e concordo") não é desfeito ao salvar
    const atual = regPorId(ctx.mod, reg.id)?.c ?? {};
    for (const k of Object.keys(atual)) {
      if (col.campos.some(f => f.k === k || k === f.k + '__rico')) continue;
      if (c[k] === reg.c[k]) final[k] = atual[k];
    }
    for (const f of col.campos) {
      if (f.t === 'numero' || f.t === 'dinheiro') { const s = (txt[f.k] ?? '').trim(); final[f.k] = s ? (f.t === 'dinheiro' ? lerValor(s) : num(s)) : ''; if (s && !isFinite(final[f.k])) final[f.k] = ''; }
      if (typeof final[f.k] === 'string' && f.t !== 'longo' && f.t !== 'cofre') final[f.k] = final[f.k].trim();
    }
    if (camposCofre.some(f => final[f.k] && !cifrado(final[f.k]))) {
      if (!(await cofreSheet.pedir())) return;
      try { for (const f of camposCofre) if (final[f.k] && !cifrado(final[f.k])) final[f.k] = cifrar(final[f.k]); }
      catch (e) { return erro(e); }
    }
    // fichas com campos do cofre (saúde, documentos do veículo...): fotos e arquivos também ficam criptografados
    let anexosFinal = anexos;
    if (protegido && anexos.some(a => !a.cofre && (a.dado || a.uri))) {
      if (!(await cofreSheet.pedir())) return;
      try { anexosFinal = []; for (const a of anexos) anexosFinal.push(await protegerAnexo(a)); }
      catch (e) { return erro(e); }
    }
    // quem não vê a ficha pode ter mudado enquanto ela estava aberta (Quem vê)
    const r: Reg = { ...reg, oc: regPorId(ctx.mod, reg.id)?.oc ?? reg.oc, c: final, anexos: anexosFinal, itens: itens.filter(i => i.text.trim()) };
    salvarReg(ctx.mod, r);
    if (!existe && col.semear) salvarVarios(ctx.mod, col.semear(ctx, r));
    col.aoSalvar?.(r, ctx);
    onClose();
    // texto 10 (E1): item NOVO com data pergunta se vai para o My Planner (nada vai sozinho). "Salvar e colocar" não pergunta.
    if (!existe && semPergunta !== true) {
      const ag = col.agenda?.(r, ctx) ?? null;
      const data = String(ag?.start ?? (col.data ? r.c[col.data] ?? '' : '')) || col.campos.filter(f => f.t === 'data' && !!f.aviso?.length).map(f => String(r.c[f.k] ?? '')).find(Boolean) || '';
      if (data) perguntarEnvio({ aba: ctx.mod, id: r.id, titulo: col.titulo(r, ctx) || t(col.um), emoji: iconeDe(col, r), data, hora: ag?.time ?? null,
        prof: profDe(ctx.mod, r.c), lista: col.id, abrir: col.agenda || listaComData(col) ? () => onAgenda(r, col) : undefined });
    }
    return r;
  }

  // Preencher pela foto (nota fiscal, receita, voucher, recibo...) com a IA do próprio usuário
  const [lendo, setLendo] = useState(false);
  const fonteIA = useFonteIA();
  // veio do botão "Cadastrar pela foto": já abre a câmera (ou a galeria) e a IA preenche
  useEffect(() => {
    if (!reg.c._ia) return;
    setC(x => { const { _ia, ...resto } = x; void _ia; return resto; });
    const tm = setTimeout(() => { preencherComIA(); }, 450);
    return () => clearTimeout(tm);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  async function preencherComIA() {
    if (!(await iaPronta())) return Alert.alert(t('IA não ligada'), t('Ligue a sua IA em Ajustes › Inteligência artificial.'));
    const ja = anexos.find(a => (temLocal(a) || a.cofre) && (a.mime?.startsWith('image/') || a.mime === 'application/pdf'));
    try {
      // tirar a foto agora, escolher da galeria ou usar o arquivo que já está na ficha
      const r = await fonteIA.escolher(protegido, ja);
      if (!r) return;
      const arq = r.arq;
      if (arq !== ja) setAnexos(l => [...l, arq]);
      if (r.aviso) Alert.alert(t('Arquivo adicionado'), r.aviso);
      if (arq.cofre && !(await cofreSheet.pedir())) return;
      setLendo(true);
      const lido = await lerFicha(t(col.um), col.campos.filter(f => !f.obrigatorio), { mime: arq.mime || 'image/jpeg', base64: await lerBase64(arq) }, !!col.itens);
      const vazio = (v: unknown) => v === '' || v == null || (Array.isArray(v) && !v.length) || v === false;
      setC(x => {
        let n = { ...x };
        // 0.65: campo "vazio" também é o que ainda está com o valor inicial da ficha nova (data de hoje, km do veículo...)
        const inicial: Record<string, any> = !existe && col.novo ? col.novo(ctx, paiReg) ?? {} : {};
        for (const [k, v] of Object.entries(lido.c)) if (vazio(x[k]) || (k in inicial && x[k] === inicial[k])) { n[k] = v; if (col.ajustar) n = col.ajustar(n, k, ctx, paiReg); }
        // auditoria 0.77: os números que mudaram (lidos ou recalculados) vão também para o texto do campo,
        // que é o que o Salvar grava (antes o km lido voltava para o km antigo e a próxima troca ficava vazia)
        const muda = col.campos.filter(f => (f.t === 'numero' || f.t === 'dinheiro') && n[f.k] !== x[f.k]);
        if (muda.length) setTxt(s => ({ ...s, ...Object.fromEntries(muda.map(f => [f.k, n[f.k] === '' || n[f.k] == null ? '' : String(n[f.k]).replace('.', ',')])) }));
        return n;
      });
      if (lido.itens.length) setItens(l => [...l, ...lido.itens.map(text => ({ id: uid(), text, done: false }))]);
      Alert.alert(t('Ficha preenchida'), Object.keys(lido.c).length || lido.itens.length ? t('Confira os campos antes de salvar.') : t('A IA não encontrou dados para esta ficha no arquivo.'));
    } catch (e) { erro(e); }
    finally { setLendo(false); }
  }

  // 0.65 (Texto 3 A): toda ficha termina com "Anotações" (as que já tinham o campo, ele vai para o fim)
  const kNotas = col.chaveAnotacoes ?? 'notas';   // texto 12: a avaliação (Professor) guarda as anotações em outra chave
  const camposComNotas = [...col.campos.filter(f => f.k !== kNotas), col.campos.find(f => f.k === kNotas) ?? { k: kNotas, r: N_('Anotações'), t: 'longo' as const }];
  const juntos = new Set(col.campos.map(f => f.comOpcao).filter(Boolean));   // texto 15 B4.6: mostrados ao lado do número
  const visiveis = camposComNotas.filter(f => !juntos.has(f.k) && (!f.mostrar || f.mostrar(c, ctx, paiReg)) && (!compart || ac.segs.has(segDoCampo(f))));
  const desenhar = (f: Campo) => (
        <View key={f.k} style={{ gap: 6 }}>
          {!!f.r && <Text style={{ color: C.sub, fontSize: 13 }}>{f.rDe ? f.rDe(c) : t(f.r)}</Text>}
          {(() => {
            const v = c[f.k];
            // texto 15 B4.6b: valor calculado (ex.: pelos módulos do curso): aparece travado, com a nota
            const nota = f.calculado?.(c, ctx, reg);
            if (nota) return (<>
              <View style={{ backgroundColor: C.card2, borderRadius: 10, paddingHorizontal: 12, paddingVertical: 10, opacity: 0.8 }}><Text style={{ color: C.text, fontWeight: '700' }}>🔒 {f.t === 'dinheiro' ? din(v || 0, moedaDo(c, f.k, ctx.moeda)) : v === '' || v == null ? '0' : String(v)}</Text></View>
              <Text style={{ color: C.mute, fontSize: 12 }}>{t(nota)}</Text>
            </>);
            const comOp = f.comOpcao ? col.campos.find(x => x.k === f.comOpcao) : undefined;
            const opJunto = comOp?.op ? (
              <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
                {comOp.op.map(o => <Chip key={o} small cor={cor} on={c[comOp.k] === o} label={t(o)} onPress={() => set(comOp.k, o)} />)}
              </View>) : null;
            switch (f.t) {
              case 'icone': return <CampoIcone valor={v || '📁'} onMudar={x => set(f.k, x)} cor={c.cor || cor} rapidos={['📁', '📚', '🎓', '🎬', '⭐', '📝', '💡', '🧠']} />;
              case 'cor': return (
                <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
                  {PALETA.map(p => <Pressable key={p} onPress={() => set(f.k, p)} accessibilityLabel={p} hitSlop={4}
                    style={{ width: 32, height: 32, borderRadius: 16, backgroundColor: p, borderWidth: v === p ? 3 : 0, borderColor: C.text }} />)}
                </View>);
              case 'endereco': return (<>
                <Field multiline value={v ?? ''} onChangeText={x => set(f.k, x)} placeholder={f.ph ? t(f.ph) : t('Rua, número, bairro, cidade (ou o nome do lugar)')} style={{ minHeight: 48 }} />
                {!!String(v ?? '').trim() && <Chip small cor={cor} on={false} label={'🗺️ ' + t('Ver a rota')} onPress={() => abrirRota(String(v))} />}
              </>);
              case 'longo':
                // 11f H: anotação, observação e texto livre usam o editor das páginas (texto simples continua na chave do campo)
                if (f.simples || protegido || (v != null && typeof v === 'object')) return <Field multiline value={v ?? ''} onChangeText={x => set(f.k, x)} placeholder={f.ph ? t(f.ph) : ''} style={{ minHeight: 90, textAlignVertical: 'top' }} />;
                return <CampoRico valor={v} rico={c[chaveRica(f.k)]} cor={cor} aba={ctx.mod} dica={f.ph ? t(f.ph) : undefined} nome={t(f.r)}
                  onChange={(tx, rico) => { set(f.k, tx); set(chaveRica(f.k), rico); }} />;
              case 'numero': if (f.ano) {
                // texto 15 C2: ano de lançamento: campo vazio, teclado de números, só 4 dígitos
                return <Field value={txt[f.k] ?? ''} onChangeText={x => { const d = x.replace(/\D/g, '').slice(0, 4); setTxt(s => ({ ...s, [f.k]: d })); set(f.k, d ? num(d) : ''); }}
                  keyboardType="number-pad" maxLength={4} placeholder={f.ph ? t(f.ph) : t('Ex.: {a}', { a: today().slice(0, 4) })} style={{ maxWidth: 160 }} />;
              } // eslint-disable-next-line no-fallthrough
              {
                // 0.65 (Texto 3 A): número com − e +; duração (minutos) anda de 15 em 15 e mostra em horas
                const passo = f.un === 'min' ? 15 : 1, atual = num(v);
                const por = (n: number) => { const x = Math.max(0, Math.round((atual + n) * 100) / 100); set(f.k, x); setTxt(s => ({ ...s, [f.k]: String(x) })); };
                const bt = (rot: string, n: number) => <Pressable onPress={() => por(n)} hitSlop={6} style={{ width: 40, height: 40, borderRadius: 20, backgroundColor: C.card2, alignItems: 'center', justifyContent: 'center' }}><Text style={{ color: C.text, fontSize: 20, fontWeight: '700' }}>{rot}</Text></Pressable>;
                return (<>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                    {bt('−', -passo)}
                    <Field value={txt[f.k] ?? ''} onChangeText={x => { setTxt(s => ({ ...s, [f.k]: x })); set(f.k, x.trim() ? num(x) : ''); }}
                      keyboardType="decimal-pad" placeholder={f.ph ? t(f.ph) : '0'} style={{ flex: 1, textAlign: 'center' }} />
                    {bt('+', passo)}
                    {f.un === 'min' && atual > 0 ? <Text style={{ color: C.sub, fontSize: 12 }}>{durLabel(atual)}</Text> : f.un ? <Text style={{ color: C.sub, fontSize: 12 }}>{t(f.un)}</Text> : null}
                  </View>
                  {opJunto}
                </>);
              }
              case 'dinheiro': {
                const campo = <Field value={txt[f.k] ?? ''} onChangeText={x => { setTxt(s => ({ ...s, [f.k]: x })); set(f.k, x.trim() ? (f.t === 'dinheiro' ? lerValor(x) : num(x)) : ''); }}
                  keyboardType="decimal-pad" placeholder={f.ph ? t(f.ph) : f.t === 'dinheiro' ? din(0, moedaDo(c, f.k, ctx.moeda)) : f.un ? t(f.un) : ''} style={comMoeda ? { flex: 1 } : undefined} />;
                // 0.69 (B1): a moeda na frente do valor, com opção de trocar só este valor
                if (!comMoeda) return campo;
                return (
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                    <Chip cor={cor} on={false} label={`${simboloMoeda(moedaDo(c, f.k, ctx.moeda))} ▾`} onPress={() => setMoedaCampo(f.k)} />
                    {campo}
                  </View>);
              }
              case 'data': {
                // texto 10 (A4): alertas da data (o mesmo editor da Nova tarefa); com horário junto, o editor vem depois do horário
                const hd = horaDoCampo(col, f);
                return (<>
                <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
                  <Chip cor={cor} on={!!v} label={`📅 ${v ? brYmd(v) : t('Escolher data')}`} onPress={() => setPicker({ k: f.k, mode: 'date' })} />
                  {!v && !f.semHoje && <Chip cor={cor} on={false} label={t('Hoje')} onPress={() => set(f.k, today())} />}
                  {!v && f.atalhosMeses?.map(n => <Chip key={n} cor={cor} on={false} label={n === 1 ? t('Em 1 mês') : t('Em {n} meses', { n })} onPress={() => set(f.k, emMeses(n))} />)}
                  {!!v && <Chip cor={cor} on={false} label={t('Limpar')} onPress={() => set(f.k, '')} />}
                </View>
                {!soVer && campoComAlerta(col, f) && (!hd || !visiveis.includes(hd)) && <AlertasDaFicha col={col} f={f} c={c} mod={ctx.mod} cor={cor} setC={setC} />}
                </>);
              }
              case 'hora': {
                const dh = col.campos.find(d => horaDoCampo(col, d) === f);
                return (<>
                <View style={{ flexDirection: 'row', gap: 8 }}>
                  <Chip cor={cor} on={!!v} label={`🕐 ${v ? horaTela(v) : t('Escolher hora')}`} onPress={() => setPicker({ k: f.k, mode: 'time' })} />
                  {!!v && <Chip cor={cor} on={false} label={t('Limpar')} onPress={() => set(f.k, '')} />}
                </View>
                {!soVer && !!dh && visiveis.includes(dh) && campoComAlerta(col, dh) && <AlertasDaFicha col={col} f={dh} c={c} mod={ctx.mod} cor={cor} setC={setC} />}
                </>);
              }
              case 'opcao': if (f.outro) {
                // texto 13 A: "Outro" abre a caixa para escrever; o que já foi escrito em outras fichas aparece como opção
                const base = (f.opDe ? f.opDe(ctx, ctx.porId(reg.pai), c) : f.op ?? []).filter(o => o !== 'Outro');   // texto 19 (D8): também nas opções que vêm dos dados
                const escritos = [...new Set(ctx.regs.filter(x => x.col === col.id && x.id !== reg.id).map(x => String(x.c[f.k] ?? '').trim()))]
                  .filter(o => o && o !== 'Outro' && !base.includes(o)).sort((x, y) => x.localeCompare(y));
                const escrevendo = outroTxt[f.k] !== undefined;
                const proprio = !!v && v !== 'Outro' && !base.includes(v) && !escritos.includes(v) && !escrevendo ? [String(v)] : [];
                const escolher = (o: string) => { setOutroTxt(x => { const y = { ...x }; delete y[f.k]; return y; }); set(f.k, v === o ? '' : o); };
                return (<>
                  <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
                    {base.map(o => <Chip key={o} small cor={cor} on={v === o && !escrevendo} label={t(o)} onPress={() => escolher(o)} />)}
                    {[...escritos, ...proprio].map(o => <Chip key={'e' + o} small cor={cor} on={v === o && !escrevendo} label={o} onPress={() => escolher(o)} />)}
                    <Chip small cor={cor} on={escrevendo || v === 'Outro'} label={t('Outro')}
                      onPress={() => { if (escrevendo) { escolher(''); return; } setOutroTxt(x => ({ ...x, [f.k]: '' })); set(f.k, 'Outro'); }} />
                  </View>
                  {escrevendo && <Field autoFocus value={outroTxt[f.k]} placeholder={t('Escreva (ex.: filosofia)')}
                    onChangeText={x => { setOutroTxt(o => ({ ...o, [f.k]: x })); set(f.k, x.trim() ? x.trimStart() : 'Outro'); }} />}
                </>);
              }
              return (
                <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
                  {(f.opDe ? f.opDe(ctx, ctx.porId(reg.pai), c) : f.op ?? []).map(o => <Chip key={o} small cor={cor} on={v === o} label={f.opDe && !f.traduzir ? o : t(o)} onPress={() => { const nv = v === o ? '' : o; set(f.k, nv); if (nv) f.aoEscolher?.(nv); }} />)}
                  {!!f.opDe && !f.opDe(ctx, ctx.porId(reg.pai), c).length && <Text style={{ color: C.mute, fontSize: 12 }}>{f.semOpcoes ? t(f.semOpcoes) : t('Nenhuma opção ainda.')}</Text>}
                </View>);
              case 'multi': return (
                <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
                  {(f.opDe ? f.opDe(ctx, ctx.porId(reg.pai), c) : f.op ?? []).map(o => { const l: string[] = Array.isArray(v) ? v : []; return <Chip key={o} small cor={cor} on={l.includes(o)} label={f.opDe && !f.traduzir ? o : t(o)} onPress={() => set(f.k, l.includes(o) ? l.filter(x => x !== o) : [...l, o])} />; })}
                  {!!f.opDe && !!f.semOpcoes && !f.opDe(ctx, ctx.porId(reg.pai), c).length && <Text style={{ color: C.mute, fontSize: 12 }}>{t(f.semOpcoes)}</Text>}
                </View>);
              case 'nomes': return <CampoNomes valor={v} auto={f.nomesAuto?.(ctx, reg) ?? []} cor={cor} onMudar={x => set(f.k, x)} botao={f.botaoAdd ? t(f.botaoAdd) : undefined} ph={f.ph ? t(f.ph) : undefined} sugeridos={f.sugeridos?.(ctx)} />;
              case 'doc': return <CampoDoc valor={v} cor={cor} empresa={c.tipo === 'Empresa'} novo={!existe || !reg.c[f.k]} onMudar={x => set(f.k, x)} />;
              case 'cep': return <CampoCep valor={v} cor={cor} onMudar={x => set(f.k, x)} onAchar={e => { for (const [k, alvo] of Object.entries(f.cep ?? {}) as [keyof typeof e, string][]) if (alvo && e[k]) set(alvo, e[k]); }} />;
              case 'sim': if (f.interruptor) {
                // texto 19 (G/I): interruptor; com dataAoLigar, mostra quando foi ligado (ex.: "Visitado em 24/10/2026")
                const quando = f.dataAoLigar ? String(c[f.dataAoLigar] ?? '') : '';
                return (
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                    <Switch value={!!v} onValueChange={x => set(f.k, x)} trackColor={{ true: cor }} accessibilityLabel={t(f.r)} />
                    <Text style={{ color: v ? C.text : C.sub, fontSize: 14, flexShrink: 1 }}>{v ? (f.rotuloLigado && quando ? t(f.rotuloLigado, { d: brYmd(quando) }) : t('Sim')) : t('Não')}</Text>
                  </View>);
              }
              return <Chip cor={cor} on={!!v} label={v ? '✓ ' + t('Sim') : t('Não')} onPress={() => set(f.k, !v)} />;
              case 'estrelas': return (
                <View style={{ flexDirection: 'row', gap: 4 }}>
                  {[1, 2, 3, 4, 5].map(n => <Pressable key={n} onPress={() => set(f.k, v === n ? 0 : n)} hitSlop={4}><Text style={{ fontSize: 26, color: n <= num(v) ? '#F59E0B' : C.mute }}>★</Text></Pressable>)}
                </View>);
              case 'fuso': return (
                <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
                  <Chip cor={cor} on={!!v} label={`🌐 ${v ? nomeFuso(v) : t('Escolher fuso')}`} onPress={() => setFuso(f.k)} />
                  {!!v && <Chip cor={cor} on={false} label={t('Limpar')} onPress={() => set(f.k, '')} />}
                </View>);
              case 'ref': {
                const ops = ctx.regs.filter(r => r.col === f.ref && (!f.refPai || r.pai === reg.pai));
                const cr = DEFS[ctx.mod].colecoes.find(x => x.id === f.ref);
                if (!ops.length) return <Text style={{ color: C.mute, fontSize: 12 }}>{t('Cadastre primeiro em {nome}.', { nome: cr ? t(cr.nome) : '' })}</Text>;
                return (
                  <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
                    {ops.map(o => <Chip key={o.id} small cor={cor} on={v === o.id} label={cr ? cr.titulo(o, ctx) || t(cr.um) : o.id} onPress={() => set(f.k, v === o.id ? '' : o.id)} />)}
                  </View>);
              }
              case 'cofre':
                if (fechado(f.k)) return <Chip cor={cor} on={false} label={'🔒 ' + t('Abrir o cofre para ver')} onPress={() => cofreSheet.pedir()} />;
                if (compart) return <Field multiline value={v ?? ''} onChangeText={x => set(f.k, x)} placeholder={f.ph ? t(f.ph) : ''} style={{ minHeight: 70, textAlignVertical: 'top' }} />;
                return (<>
                  <Field multiline value={v ?? ''} onChangeText={x => set(f.k, x)} placeholder={f.ph ? t(f.ph) : ''} style={{ minHeight: 70, textAlignVertical: 'top' }} />
                  <Text style={{ color: C.mute, fontSize: 11 }}>🔐 {cofre.configurado ? t('Guardado no cofre, com criptografia de ponta a ponta.') : t('Ao salvar, você cria a senha do cofre. Este campo é criptografado no dispositivo.')}</Text>
                </>);
              default: if (f.sugestoes) {
                // texto 15 C3: campo livre, com o que já foi usado nas outras fichas como opções rápidas
                const usados = [...new Set(ctx.regs.filter(x => x.col === col.id && x.id !== reg.id).map(x => String(x.c[f.k] ?? '').trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b)).slice(0, 12);
                return (<>
                  <Field value={v ?? ''} onChangeText={x => set(f.k, x)} placeholder={f.ph ? t(f.ph) : ''} />
                  {!!usados.length && <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
                    {usados.map(o => <Chip key={o} small cor={cor} on={String(v ?? '').trim() === o} label={o} onPress={() => set(f.k, o)} />)}
                  </View>}
                </>);
              }
                return <Field value={v ?? ''} onChangeText={x => set(f.k, x)} placeholder={f.ph ? t(f.ph) : ''}
                  keyboardType={f.t === 'telefone' ? 'phone-pad' : f.t === 'email' ? 'email-address' : f.t === 'link' ? 'url' : 'default'}
                  autoCapitalize={f.t === 'email' || f.t === 'link' ? 'none' : 'sentences'} autoCorrect={f.t === 'texto'} />;
            }
          })()}
          {f.t === 'email' && ctx.mod === 'clientes' && !emailOk(c[f.k]) && <Text style={{ color: C.warn, fontSize: 12 }}>✉️ {t('Parece que falta algo neste e-mail (ex.: nome@site.com).')}</Text>}
          {!!f.dica && <Text style={{ color: C.mute, fontSize: 12 }}>{t(f.dica)}</Text>}
        </View>
  );
  return (
    <PaginaRolavel Rolagem={ScrollArrastavel} keyboardShouldPersistTaps="handled" contentContainerStyle={{ padding: 16, gap: 10, paddingBottom: 80 }}>
      {compart && (
        <View style={{ backgroundColor: alpha(cor, 0.1), borderRadius: 12, padding: 10, gap: 2 }}>
          <Text style={{ color: C.text, fontWeight: '700', fontSize: 13 }}>👥 {soVer ? t('Compartilhado no grupo · só leitura') : t('Compartilhado no grupo')}</Text>
          {!!ac.nome && <Text style={{ color: C.sub, fontSize: 12 }}>{t('Seu papel: {p}', { p: t(ac.nome) })}</Text>}
          {!!reg.por && !sozinhoNoGrupo && <Text style={{ color: C.sub, fontSize: 12 }}>{t('Alterado por último por {nome}', { nome: reg.por })}</Text>}
          {existe && !!reg.g && <QuemVe reg={reg} mod={ctx.mod} cor={cor} />}
        </View>
      )}
      {col.anexos && !soVer && (
        <Pressable onPress={preencherComIA} disabled={lendo} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, padding: 12, borderRadius: 12, backgroundColor: alpha(cor, 0.15) }}>
          {lendo ? <ActivityIndicator color={cor} /> : <Text style={{ fontSize: 18 }}>🤖</Text>}
          <Text style={{ color: C.text, fontWeight: '700', flex: 1 }}>{lendo ? t('Lendo o documento...') : t('Preencher pela foto com a minha IA')}</Text>
        </Pressable>
      )}
      {!!col.extraFicha && !soVer && <col.extraFicha c={c} cor={cor} mudar={patch => {
        // texto 14 J: dados vindos de fora (DOI, link) entram na ficha para a pessoa conferir antes de salvar
        for (const [k, v] of Object.entries(patch)) set(k, v);
        const nums = col.campos.filter(f => (f.t === 'numero' || f.t === 'dinheiro') && f.k in patch);
        if (nums.length) setTxt(x => ({ ...x, ...Object.fromEntries(nums.map(f => [f.k, patch[f.k] === '' || patch[f.k] == null ? '' : String(patch[f.k]).replace('.', ',')])) }));
      }} />}
      <View pointerEvents={soVer ? 'none' : 'auto'} style={{ gap: 10 }}>
      {(() => {
        // texto 13 D: campos do mesmo bloco ficam juntos (na ordem do bloco), num cartão que a pessoa oculta e mostra
        const feitos = new Set<string>();
        return visiveis.map(f => {
          if (feitos.has(f.k)) return null;
          const b = col.blocos?.find(x => x.campos.includes(f.k));
          if (!b) return desenhar(f);
          const doBloco = b.campos.map(k => visiveis.find(x => x.k === k)).filter((x): x is Campo => !!x);
          doBloco.forEach(x => feitos.add(x.k));
          return (
            <Recolhivel key={'b' + b.titulo} visual="cartao" inicial={!b.fechado} chave={`ficha:${ctx.mod}:${col.id}:bloco:${b.campos[0]}`} titulo={t(b.titulo)}
              resumo={b.resumo ? b.resumo(c) || t('opcional') : undefined}>
              {doBloco.map(desenhar)}
            </Recolhivel>);
        });
      })()}

      {!!col.itens && (
        <View style={{ gap: 6 }}>
          <Text style={{ color: C.sub, fontSize: 13 }}>{t(col.itens)}</Text>
          {/* texto 26 (REGRAS 2): sem ⠿; segurar na parte livre e arrastar move; deslizar → exclui, ← duplica */}
          <Ordenavel itens={itens} chave={it => it.id} alca gap={6} onOrdem={setItens} render={(it, i, ia) => {
            const tirar = () => excluirPequeno(!!it.text.trim(), () => { setItens(l => l.filter(x => x.id !== it.id)); return () => setItens(l => (l.some(x => x.id === it.id) ? l : inserirEm(l, i, it))); });
            return (
            <ItemDeslizar onExcluir={tirar} onDuplicar={() => setItens(l => inserirEm(l, i + 1, { ...it, id: uid() }))}>
            <View {...ia.alca} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: C.card, borderRadius: 10, paddingHorizontal: 10, paddingVertical: 4 }}>
              <Pressable onPress={() => setItens(l => l.map((x, j) => (j === i ? { ...x, done: !x.done } : x)))} hitSlop={8}>
                <Text style={{ fontSize: 18, color: it.done ? cor : C.sub }}>{it.done ? '☑' : '☐'}</Text>
              </Pressable>
              <Field {...semArrastar} value={it.text} onChangeText={x => setItens(l => l.map((y, j) => (j === i ? { ...y, text: x } : y)))} style={{ flex: 1, backgroundColor: 'transparent', paddingHorizontal: 0, paddingVertical: 8, textDecorationLine: it.done ? 'line-through' : 'none' }} />
            </View>
            </ItemDeslizar>
          ); }} />
          <View style={{ flexDirection: 'row', gap: 8 }}>
            <Field value={novoItem} onChangeText={setNovoItem} placeholder={t('Novo item')} style={{ flex: 1 }} blurOnSubmit={false}
              onSubmitEditing={() => { if (novoItem.trim()) { setItens(l => [...l, { id: uid(), text: novoItem.trim(), done: false }]); setNovoItem(''); } }} />
            <Chip cor={cor} on={false} label="+" onPress={() => { if (novoItem.trim()) { setItens(l => [...l, { id: uid(), text: novoItem.trim(), done: false }]); setNovoItem(''); } }} />
          </View>
        </View>
      )}

      </View>

      {col.anexos && (
        <View style={{ gap: 6 }}>
          <Text style={{ color: C.sub, fontSize: 13 }}>{t('Fotos, arquivos e links')}</Text>
          <AnexosEditor lista={anexos} cor={cor} onChange={setAnexos} marcas={col.marcas?.map(m => t(m))} protegido={protegido} pedirCofre={() => cofreSheet.pedir()} soVer={soVer}
            juntarFotos={col.juntarFotos} nomePdf={String(c.titulo || c.nome || t(col.um))} />
          {/* texto 13 F4: emoji e texto separados, para a linha quebrar inteira (no iPhone ela aparecia cortada) */}
          {compart && !soVer && (
            <View style={{ flexDirection: 'row', gap: 4, alignItems: 'flex-start' }}>
              <Text style={{ color: C.mute, fontSize: 11, lineHeight: 15 }}>👥</Text>
              <Text style={{ color: C.mute, fontSize: 11, lineHeight: 15, flex: 1, flexShrink: 1 }}>{t('Fotos e arquivos desta ficha também vão para a pasta do grupo, para quem participa abrir.')}</Text>
            </View>)}
        </View>
      )}

      {!!col.fimFicha && existe && <col.fimFicha r={ctx.porId(reg.id) ?? reg} ctx={ctx} cor={cor} soVer={soVer} />}
      {!soVer && <Btn cor={cor} label={t('Salvar')} onPress={salvar} style={{ marginTop: 8 }} />}
      {(!!col.agenda || listaComData(col)) && <Btn outline cor={cor} label={'📅 ' + (reg.tarefa ? t('Ver no My Planner') : soVer ? t('Colocar no My Planner') : t('Salvar e colocar no My Planner'))}
        onPress={async () => { if (soVer) { onClose(); return onAgenda(reg, col); } const r = await salvar(true); if (r) onAgenda(r, col); }} />}
      {existe && !soVer && <Btn outline cor={C.danger} label={t('Excluir')} onPress={() => onExcluir(reg, col)} />}
      {anexos.some(a => !abreAqui(a)) && <Text style={{ color: C.mute, fontSize: 11 }}>{t('Alguns arquivos foram guardados em outro dispositivo.')}</Text>}

      {picker && <PickerModal mode={picker.mode} cor={cor}
        value={picker.mode === 'date' ? parseYmd(c[picker.k] || today()) : (() => { const d = new Date(); const [h, m] = String(c[picker.k] || '09:00').split(':').map(Number); d.setHours(h, m, 0, 0); return d; })()}
        onPick={d => set(picker.k, picker.mode === 'date' ? ymd(d) : hm(d))} onClose={() => setPicker(null)} />}
      <EscolherMoeda visible={!!moedaCampo} atual={moedaDo(c, moedaCampo ?? 'valor', ctx.moeda)} cor={cor} onClose={() => setMoedaCampo(null)} onEscolher={m => moedaCampo && mudarMoeda(moedaCampo, m)} />
      <FusoSheet visible={!!fuso} valor={(fuso && c[fuso]) || Intl.DateTimeFormat().resolvedOptions().timeZone} cor={cor} onClose={() => setFuso(null)} onPick={tz => fuso && set(fuso, tz)} />
      {cofreSheet.tela}
      {fonteIA.tela}
    </PaginaRolavel>
  );
}
